import { passYearForPurchase, passYearOfEnd } from '../utils/classAcademicYear';
import { isPlatformAdminEmail } from '../constants/platformAdmin';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  PAID_PLAN_MONTHLY_WON,
  PAID_PLAN_PARTIAL_DAY_RATE_WON,
  computePaidPlanPricing,
  computePaidPlanPricingExcludingIndividualCoverage,
  formatDateDot,
  formatDateKo,
  formatKrw,
  getSchoolYearPassLicenseWindow,
  getSchoolYearPassPurchaseOpensAt,
  listBillingStartMonthsInLicenseWindow,
  getGroupPassBillingDatePickBounds,
  parseLocalIsoDateOnly,
  toLocalIsoDateOnly,
  type IndividualCoverageRange,
  type PaidPlanMonthLine,
  type PaidPlanPricingResult,
} from '../utils/subscriptionPaidPlanPricing';
import { sendGroupPassPurchaseEmail } from '../services/groupPassEmailService';
import {
  addDaysStartOfDay,
  generateGroupCouponCode,
  parseBetaTesterCouponCodeInput,
  parseEventCouponCodeInput,
  parseGroupCouponCodeInput,
  PASS_CODE_BODY_LENGTH,
} from '../utils/groupPassCoupon';
import { getSession } from '../services/authService';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import {
  computeEventPassRefundPreview,
  computeGroupPassRefundPreview,
  computeIndividualPassRefundPreview,
  type PassRefundPreview,
} from '../utils/subscriptionPassRefund';

import {
  bindSubscriptionPassUserId,
  deriveActiveEventPassPeriod,
  eventPassKindLabel,
  loadSubscriptionPassState,
  saveSubscriptionPassState,
  syncSubscriptionPassEpochFromServer,
  type EventPassPurchaseRecord,
  type GroupPassPurchaseRecord,
  type IndividualPassPurchaseRecord,
} from '../utils/subscriptionPassStorage';
import {
  persistMySubscriptionPassState,
  syncMySubscriptionPassState,
} from '../services/platformUserSubscriptionPassService';
import PlatformCompanyInfo from '../components/landing/PlatformCompanyInfo';
import { PLATFORM_COMPANY } from '../constants/platformCompany';
import {
  revivePricing,
  savePendingCouponCheckout,
  savePendingGroupCheckout,
  savePendingIndividualCheckout,
  takePendingPassCheckout,
} from '../utils/pendingPassCheckout';
import {
  getBillingSettings,
  loadCachedBillingSettings,
} from '../services/billingSettingsService';
import {
  getPlatformTestPaymentPassword,
  isPlatformTestAccountEmail,
  type PlatformBillingSettings,
} from '../types/billingSettings';
import {
  isPaidEventProductSoldOut,
  type AdminPaidEventPassProduct,
} from '../utils/adminPaidEventPassProducts';
import {
  fetchActivePaidEventPassProducts,
  incrementPaidEventProductSoldRemote,
} from '../services/platformPaidEventProductsService';
import { sendBetaEventCodeRequestEmail } from '../services/betaEventCodeRequestEmailService';
import { sendBetaEventCodeIssueEmail } from '../services/betaEventCodeIssueEmailService';
import { submitBetaEventCodeRequest } from '../services/platformBetaEventCodeRequestService';
import {
  activePassBlockedMessage,
  isActivePassExistsError,
  isPassCodeRedeemExhaustedError,
  issueGroupPassCodeRemote,
  redeemEventPassCodeRemote,
  redeemGroupPassCodeRemote,
} from '../services/platformPassCodesService';
import { newAdminIssuedPassRowId } from '../utils/adminIssuedPassCodes';
import PassPlanComparisonTable from '../components/subscriptionPass/PassPlanComparisonTable';
import '../components/subscriptionPass/SubscriptionPassCards.css';
import { LEGAL_REFUND_HREF } from './LegalDocumentPage';

export type {
  EventPassPurchaseRecord,
  GroupPassPurchaseRecord,
  IndividualPassPurchaseRecord,
} from '../utils/subscriptionPassStorage';

type PurchaseModalState =
  | { kind: 'individual'; pricing: PaidPlanPricingResult }
  | {
      kind: 'event';
      productId: string;
      title: string;
      periodStart: Date;
      periodEnd: Date;
      totalWon: number;
      durationMonths: number;
    }
  | { kind: 'group'; basePricing: PaidPlanPricingResult };

function resolvePaidEventProductPeriod(
  product: AdminPaidEventPassProduct,
): { start: Date; end: Date } | null {
  const start = parseLocalIsoDateOnly(`${product.paidPlanStartMonth}-01`);
  const end = parseLocalIsoDateOnly(product.planEndIso);
  if (!start || !end) return null;
  return { start, end };
}

type RefundModalState = {
  kind: 'individual' | 'group' | 'event';
  recordId: string;
  step: 1 | 2 | 3;
  agreed: boolean;
  /** 완료 단계 표시용 */
  completedRefundWon?: number;
};

/** 시작일·종료일(포함) 기준으로 오늘이 구간 안인지 */
/** 날짜만 기준으로 d가 [rangeStart, rangeEnd] 안(양끝 포함) */
function isDateOnlyInInclusiveRange(d: Date, rangeStart: Date, rangeEnd: Date): boolean {
  const t = startOfDayOnly(d).getTime();
  return (
    t >= startOfDayOnly(rangeStart).getTime() && t <= endOfDayOnly(rangeEnd).getTime()
  );
}

function isInclusiveDateRangeActive(periodStart: Date, periodEnd: Date): boolean {
  const now = Date.now();
  const s = new Date(
    periodStart.getFullYear(),
    periodStart.getMonth(),
    periodStart.getDate(),
    0,
    0,
    0,
    0
  ).getTime();
  const e = new Date(
    periodEnd.getFullYear(),
    periodEnd.getMonth(),
    periodEnd.getDate(),
    23,
    59,
    59,
    999
  ).getTime();
  return now >= s && now <= e;
}

function startOfDayOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** 그룹 시작일 입력란: `YYYY.MM.DD` / `YYYY-MM-DD` / `YYYYMMDD` → `YYYY-MM-DD` */
function parseGroupBillingStartDraft(raw: string): string | null {
  const s = raw.trim().replace(/\s/g, '');
  if (!s) return null;
  let m = /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/.exec(s);
  if (m) {
    const iso = `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    return parseLocalIsoDateOnly(iso) ? iso : null;
  }
  m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (!m) return null;
  const iso = `${m[1]}-${m[2]}-${m[3]}`;
  return parseLocalIsoDateOnly(iso) ? iso : null;
}

function formatGroupBillingStartDraft(iso: string): string {
  const d = parseLocalIsoDateOnly(iso);
  return d ? formatDateDot(d) : iso;
}

function endOfDayOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * 환불되지 않은 건 중, 오늘이 이용 시작일 이전이고 종료일이 아직 지나지 않은 경우(결제 반영·혜택 대기).
 * 시작 월을 미래로 선택한 개별 이용권 등에서 사용합니다.
 */
function isUpcomingPaidPassWindow(periodStart: Date, periodEnd: Date): boolean {
  if (isInclusiveDateRangeActive(periodStart, periodEnd)) return false;
  const now = Date.now();
  if (now > endOfDayOnly(periodEnd).getTime()) return false;
  return now < startOfDayOnly(periodStart).getTime();
}

/** 이 브라우저(세션)에서 해당 그룹 구매 건의 쿠폰을 등록해 이용권을 쓴 경우에만 true */
function isGroupPassRegisteredOnThisSession(
  g: GroupPassPurchaseRecord,
  binding: { purchaseRecordId: string } | null
): boolean {
  return binding?.purchaseRecordId === g.id;
}

const GROUP_PASS_PURCHASER_ADMIN_LABEL = '클래스 플래너 관리자';

/** 그룹 이용권 «구매자(계정)» 표기 — 관리자 발급 시 고정 라벨, 그 외 결제 계정 이메일 */
function groupPassPurchaserAccountLabel(g: GroupPassPurchaseRecord): string {
  if (g.issuedByAdmin) return GROUP_PASS_PURCHASER_ADMIN_LABEL;
  const account = g.purchaserAccountEmail?.trim();
  if (account) return account;
  // 과거 저장분 등 purchaserAccountEmail 없을 때만(데이터 한계)
  return g.couponEmail;
}

function simpleEmailValid(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/**
 * 학년도 창에서 청구 시작 월 후보를 돌며, 제외 구간(개별·이 세션에 적용된 그룹·이벤트)을 반영한 첫 양수 요금을 사용.
 * 없으면 첫 달 기준 0원 결과를 반환합니다.
 */
function pickExcludedSchoolYearPricing(
  billingMonths: Date[],
  purchaseDate: Date,
  exclusions: IndividualCoverageRange[],
  periodEndCap: Date,
  fallbackBillingFirst: Date
): PaidPlanPricingResult {
  const seq = billingMonths.length > 0 ? billingMonths : [fallbackBillingFirst];
  for (const m of seq) {
    const p = computePaidPlanPricingExcludingIndividualCoverage(
      m,
      purchaseDate,
      exclusions,
      PAID_PLAN_MONTHLY_WON,
      PAID_PLAN_PARTIAL_DAY_RATE_WON,
      periodEndCap
    );
    if (p.totalWon > 0) return p;
  }
  return computePaidPlanPricingExcludingIndividualCoverage(
    seq[0]!,
    purchaseDate,
    exclusions,
    PAID_PLAN_MONTHLY_WON,
    PAID_PLAN_PARTIAL_DAY_RATE_WON,
    periodEndCap
  );
}

function isPaidPlanFullMonthLine(row: PaidPlanMonthLine): boolean {
  return (
    row.daysBilled > 0 &&
    row.daysBilled === row.daysInMonth &&
    row.amountWon === PAID_PLAN_MONTHLY_WON
  );
}

/** 개별 구매 모달: 일할·월정액을 각각 한 줄로 요약(월 정액 상한으로 단가×일과 다를 수 있음) */
function aggregateIndividualPricingModalLines(pricing: { lines: PaidPlanMonthLine[] }): string[] {
  let partialDays = 0;
  let partialWon = 0;
  let fullMonths = 0;
  let fullMonthsWon = 0;
  for (const row of pricing.lines) {
    if (row.daysBilled <= 0 || row.amountWon <= 0) continue;
    if (isPaidPlanFullMonthLine(row)) {
      fullMonths += 1;
      fullMonthsWon += row.amountWon;
    } else {
      partialDays += row.daysBilled;
      partialWon += row.amountWon;
    }
  }
  const out: string[] = [];
  if (partialDays > 0) {
    const linear = partialDays * PAID_PLAN_PARTIAL_DAY_RATE_WON;
    if (partialWon === linear) {
      out.push(
        `${partialDays}일 : ${formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원×${partialDays}일 = ${formatKrw(partialWon)}원`
      );
    } else {
      out.push(
        `일할 청구 합계 ${formatKrw(partialWon)}원 (${partialDays}일 구간, 월 ${formatKrw(PAID_PLAN_MONTHLY_WON)}원 상한 반영)`
      );
    }
  }
  if (fullMonths > 0) {
    out.push(
      `${fullMonths}개월 : ${formatKrw(PAID_PLAN_MONTHLY_WON)}원×${fullMonths}개월 = ${formatKrw(fullMonthsWon)}원`
    );
  }
  return out;
}

/** 영수증·로그용 `2026-05-12 15:50:48` */
function formatPaymentLogDateTime(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${day} ${hh}:${mm}:${ss}`;
}

function individualPassOrderLicId(row: { id: string; purchasedAt: Date }): string {
  const tail = row.id.replace(/[^a-zA-Z0-9_-]/g, '');
  return `lic_${row.purchasedAt.getTime()}_${tail || 'order'}`;
}

/** 개별·유료 이벤트 구매 영수증 모달용 */
type PassReceiptView = {
  id: string;
  purchasedAt: Date;
  amountWon: number;
  refundedAt?: Date;
  refundAmountWon?: number;
  productLabel: string;
};

function toPassReceiptView(
  row: {
    id: string;
    purchasedAt: Date;
    amountWon: number;
    refundedAt?: Date;
    refundAmountWon?: number;
  },
  productLabel: string,
): PassReceiptView {
  return {
    id: row.id,
    purchasedAt: row.purchasedAt,
    amountWon: row.amountWon,
    refundedAt: row.refundedAt,
    refundAmountWon: row.refundAmountWon,
    productLabel,
  };
}

/** 결제 총액(부가세 포함)에서 과세·부가세 분해(에듀코디 영수증과 동일한 11분할 방식) */
function inclusiveTotalToTaxableAndVat(totalWon: number): { taxable: number; vat: number; exempt: number } {
  const vat = Math.round(totalWon / 11);
  return { taxable: totalWon - vat, vat, exempt: 0 };
}

/** 상단 스트립·기간 봉투용 타일(달력 활성 / 결제 반영·시작 전 구간) */
type PlanStripTile = {
  key: string;
  kindLabel: string;
  /** 예: `2026학년도 이용권` */
  schoolYearTitle: string;
  periodLine: string;
  rangeStart: Date;
  rangeEnd: Date;
  phase: 'calendar-active' | 'upcoming';
  /** 그룹 이용권 타일: 구매자(계정) 한 줄 표기 */
  groupPurchaserAccountLabel?: string;
  groupCouponCode?: string;
};

/**
 * 이용 시작일 기준 학년도 라벨.
 * 학년도 Y 구간: Y년 2월 1일 ~ (Y+1)년 2월 말일.
 */
function schoolYearLabelFromPassStart(start: Date): number {
  const y = start.getFullYear();
  const m = start.getMonth();
  if (m === 0) return y - 1;
  return y;
}

function schoolYearPassTitleFromStart(start: Date): string {
  return `${schoolYearLabelFromPassStart(start)}학년도 이용권`;
}

const SubscriptionPassPage: React.FC<{
  /** 랜딩: 이용권 구입 섹션(+모달)만 표시 */
  variant?: 'full' | 'landing-purchase';
  /** true면 결제하기·쿠폰 등록 시 로그인 유도만 하고 진행하지 않음 */
  requireLoginForCheckout?: boolean;
  /** 랜딩에서 로그인 모달 열기 */
  onRequestLogin?: () => void;
  /** 학교 스코프에 남은 유료 이벤트 상품을 플랫폼 키로 이전·합칠 때 사용 */
  workspaceId?: string;
}> = ({
  variant = 'full',
  requireLoginForCheckout = false,
  onRequestLogin,
  workspaceId,
}) => {
  const isLandingPurchase = variant === 'landing-purchase';
  const { toastState, showInfo } = useAutoStatusToast(3500);
  const resumeCheckoutRef = useRef(false);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [metaPlanPro, setMetaPlanPro] = useState(false);
  const [billing, setBilling] = useState<PlatformBillingSettings>(() => loadCachedBillingSettings());

  const promptLoginForCheckout = useCallback(() => {
    onRequestLogin?.();
  }, [onRequestLogin]);
  const [modal, setModal] = useState<PurchaseModalState | null>(null);
  const [groupForm, setGroupForm] = useState({
    groupName: '',
    email: '',
    quantity: 1,
    agreeActivation: false,
    agreeRefund: false,
    billingStartIso: toLocalIsoDateOnly(new Date()),
  });
  /** 개별·유료 이벤트 구매 모달: 환불 정책 동의 */
  const [individualAgreeRefund, setIndividualAgreeRefund] = useState(false);
  /** 그룹 시작일 입력 초안(빈 문자열 허용 — 지우고 다시 입력) */
  const [groupBillingStartDraft, setGroupBillingStartDraft] = useState('');
  /** 관리자 «유료 이벤트 이용권 등록» 판매중 상품 → 이용권 카드 */
  const [paidEventProducts, setPaidEventProducts] = useState<AdminPaidEventPassProduct[]>([]);

  const [individualHistory, setIndividualHistory] = useState<IndividualPassPurchaseRecord[]>([]);
  const [groupHistory, setGroupHistory] = useState<GroupPassPurchaseRecord[]>([]);
  const [eventHistory, setEventHistory] = useState<EventPassPurchaseRecord[]>([]);
  /** 활성 이벤트 창(이력에서 파생·호환용) */
  const [eventPassPeriod, setEventPassPeriod] = useState<{
    periodStart: Date;
    periodEnd: Date;
  } | null>(null);
  /**
   * 이 브라우저(세션)에서 그룹 쿠폰으로 유료 플랜을 켠 구매 건 id.
   * 이용권 수만큼은 `activatedCount`로 풀 관리, 계정당 1이용권은 세션에서 한 번만 적용.
   */
  const [sessionGroupPassBinding, setSessionGroupPassBinding] = useState<{
    purchaseRecordId: string;
    appliedAt: Date;
  } | null>(null);
  const [passHydrated, setPassHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [session, settings] = await Promise.all([getSession(), getBillingSettings()]);
        if (cancelled) return;
        const uid = session?.user?.id ?? null;
        const email = session?.user?.email ?? null;
        const meta = (session?.user?.user_metadata ?? {}) as Record<string, unknown>;
        setSessionUserId(uid);
        bindSubscriptionPassUserId(uid);
        setSessionEmail(email);
        setMetaPlanPro(meta.cp_plan_tier === 'pro');
        setBilling(settings);
        syncSubscriptionPassEpochFromServer(uid, meta.cp_pass_epoch);
        const syncResult = uid ? await syncMySubscriptionPassState(uid) : null;
        const loaded = syncResult?.state ?? loadSubscriptionPassState(null);
        if (cancelled) return;
        setIndividualHistory(loaded.individualHistory);
        setGroupHistory(loaded.groupHistory);
        setEventHistory(loaded.eventHistory);
        setEventPassPeriod(loaded.eventPassPeriod);
        setSessionGroupPassBinding(loaded.sessionGroupPassBinding);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          const loaded = loadSubscriptionPassState(null);
          setIndividualHistory(loaded.individualHistory);
          setGroupHistory(loaded.groupHistory);
          setEventHistory(loaded.eventHistory);
          setEventPassPeriod(loaded.eventPassPeriod);
          setSessionGroupPassBinding(loaded.sessionGroupPassBinding);
        }
      } finally {
        if (!cancelled) setPassHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadPaidEventProducts = useCallback(() => {
    void (async () => {
      try {
        const rows = await fetchActivePaidEventPassProducts();
        setPaidEventProducts(rows);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    reloadPaidEventProducts();
    const onFocus = () => reloadPaidEventProducts();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [reloadPaidEventProducts]);

  const isTestAccount = isPlatformTestAccountEmail(sessionEmail);
  const paidCheckoutEnabled = billing.paid_payment_enabled || isTestAccount;
  const paymentMode = billing.payment_mode;
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  /** 쿠폰 모달: 그룹 쿠폰 vs 이벤트 코드 */
  const [couponModalKind, setCouponModalKind] = useState<'group' | 'event' | 'beta'>('group');
  /** 그룹 이용권 쿠폰 전체 코드 입력 (예: GRP-XXXXXXXX) */
  const [couponCodeInput, setCouponCodeInput] = useState('');
  /** 베타 잠금: 이벤트 코드 요청 모달 */
  const [betaCodeRequestOpen, setBetaCodeRequestOpen] = useState(false);
  const [betaCodeRequestForm, setBetaCodeRequestForm] = useState({
    fullName: '',
    email: '',
    organization: '',
    note: '',
    agreed: false,
  });
  const [betaCodeRequestSubmitting, setBetaCodeRequestSubmitting] = useState(false);
  const [betaCodeRequestError, setBetaCodeRequestError] = useState<string | null>(null);
  const [refundModal, setRefundModal] = useState<RefundModalState | null>(null);
  const [groupPurchaseSubmitting, setGroupPurchaseSubmitting] = useState(false);
  /** 테스트 모드: 결제하기 → 비밀번호 확인 후 데모 발급 */
  const [testPayPending, setTestPayPending] = useState<
    'individual' | 'event' | 'group' | null
  >(null);
  const [testPayPassword, setTestPayPassword] = useState('');
  const [testPayError, setTestPayError] = useState<string | null>(null);
  /** 현재 플랜 블럭 하단「결제 내역 확인」— 개인·그룹 구매 이력 섹션 펼침 */
  const [passPaymentHistoryOpen, setPassPaymentHistoryOpen] = useState(false);
  /** 개별 이용권 이력: 접기 시 활성 건만 / 펼치면 전체 */
  const [individualHistoryDetailOpen, setIndividualHistoryDetailOpen] = useState(false);
  /** 그룹 이용권 이력: 접기 시 이 세션에 쿠폰 등록된 활성·시작 전 구간 + 미등록(유효) 건 / 펼치면 전체 */
  const [groupHistoryDetailOpen, setGroupHistoryDetailOpen] = useState(false);
  /** 그룹 이력 카드(구매 내역) 쿠폰 코드 복사 피드백용 */
  const [groupHistoryCouponCopiedId, setGroupHistoryCouponCopiedId] = useState<string | null>(null);
  const [individualReceipt, setIndividualReceipt] = useState<PassReceiptView | null>(null);
  const [receiptBuyerName, setReceiptBuyerName] = useState('구매자');
  const [receiptBuyerEmail, setReceiptBuyerEmail] = useState('—');

  /** 개별 플랜 예상가·모달 산정 시 제외할 이미 덮인 기간(미환불 개별 + 이 세션에 적용된 그룹 + 이벤트) */
  const individualPricingExclusions = useMemo((): IndividualCoverageRange[] => {
    const ind = individualHistory
      .filter((r) => !r.refundedAt)
      .map((r) => ({ periodStart: r.periodStart, periodEnd: r.periodEnd }));
    const grp = groupHistory
      .filter((g) => !g.refundedAt && isGroupPassRegisteredOnThisSession(g, sessionGroupPassBinding))
      .map((g) => ({ periodStart: g.activationPeriodStart, periodEnd: g.activationPeriodEnd }));
    const ev: IndividualCoverageRange[] =
      eventPassPeriod != null
        ? [{ periodStart: eventPassPeriod.periodStart, periodEnd: eventPassPeriod.periodEnd }]
        : [];
    return [...ind, ...grp, ...ev];
  }, [individualHistory, groupHistory, sessionGroupPassBinding, eventPassPeriod]);

  /**
   * 달력 연도 cy 기준: cy·cy+1 학년도 이용권 플랜 카드용 가격·구매 가능 여부.
   * (cy+1)학년도는 cy+1년 1월 1일부터 구매 가능.
   */
  const passPlanUi = useMemo(() => {
    const t = new Date();
    const cy = t.getFullYear();
    const leftSy = cy;
    const rightSy = cy + 1;
    const leftW = getSchoolYearPassLicenseWindow(leftSy);
    const rightW = getSchoolYearPassLicenseWindow(rightSy);
    const excl = individualPricingExclusions;

    const monthsL = listBillingStartMonthsInLicenseWindow(t, leftW.start, leftW.end);
    const pageLeft = pickExcludedSchoolYearPricing(monthsL, t, excl, leftW.end, leftW.start);
    const fullYearLeft = computePaidPlanPricing(
      leftW.start,
      leftW.start,
      PAID_PLAN_MONTHLY_WON,
      PAID_PLAN_PARTIAL_DAY_RATE_WON,
      leftW.end
    );
    const strikeLeft = pageLeft.totalWon > 0 && pageLeft.totalWon < fullYearLeft.totalWon;

    const purchasableLeft = monthsL.some(
      (m) =>
        computePaidPlanPricingExcludingIndividualCoverage(
          m,
          t,
          excl,
          PAID_PLAN_MONTHLY_WON,
          PAID_PLAN_PARTIAL_DAY_RATE_WON,
          leftW.end
        ).totalWon > 0
    );

    const opensRightAt = getSchoolYearPassPurchaseOpensAt(rightSy);
    const canBuyRight = startOfDayOnly(t).getTime() >= startOfDayOnly(opensRightAt).getTime();

    const fullYearRight = computePaidPlanPricing(
      rightW.start,
      rightW.start,
      PAID_PLAN_MONTHLY_WON,
      PAID_PLAN_PARTIAL_DAY_RATE_WON,
      rightW.end
    );

    const monthsR = listBillingStartMonthsInLicenseWindow(t, rightW.start, rightW.end);
    const pageRight: PaidPlanPricingResult = canBuyRight
      ? pickExcludedSchoolYearPricing(monthsR, t, excl, rightW.end, rightW.start)
      : fullYearRight;
    const strikeRight =
      canBuyRight && pageRight.totalWon > 0 && pageRight.totalWon < fullYearRight.totalWon;

    const purchasableRight =
      canBuyRight &&
      monthsR.some(
        (m) =>
          computePaidPlanPricingExcludingIndividualCoverage(
            m,
            t,
            excl,
            PAID_PLAN_MONTHLY_WON,
            PAID_PLAN_PARTIAL_DAY_RATE_WON,
            rightW.end
          ).totalWon > 0
      );

    const todayInLeft = isDateOnlyInInclusiveRange(t, leftW.start, leftW.end);
    const todayInRight = isDateOnlyInInclusiveRange(t, rightW.start, rightW.end);

    /**
     * 그룹 이용권 카드·1인당 예상액: 본인 보유 구간과 무관하게 오늘 결제일 기준으로 산정.
     * (다른 계정에 코드를 나눠 줄 수 있으므로 개인 커버리지를 제외하지 않음)
     */
    const groupPurchaseDay = startOfDayOnly(t);
    const groupBillingMonthFirst = new Date(
      groupPurchaseDay.getFullYear(),
      groupPurchaseDay.getMonth(),
      1
    );
    const groupPageLeft = computePaidPlanPricing(
      groupBillingMonthFirst,
      t,
      PAID_PLAN_MONTHLY_WON,
      PAID_PLAN_PARTIAL_DAY_RATE_WON,
      leftW.end,
      groupPurchaseDay
    );
    const groupStrikeLeft =
      groupPageLeft.totalWon > 0 && groupPageLeft.totalWon < fullYearLeft.totalWon;

    /** 좌·우 유료 카드에 둘째 점선(첫 점선 아래 안내 블록 뒤)을 같이 둘지 여부 */
    const eitherSecondDash = strikeLeft || !canBuyRight || strikeRight;

    return {
      cy,
      leftSy,
      rightSy,
      leftW,
      rightW,
      pageLeft,
      fullYearLeft,
      strikeLeft,
      purchasableLeft,
      pageRight,
      fullYearRight,
      strikeRight,
      purchasableRight,
      canBuyRight,
      todayInLeft,
      todayInRight,
      eitherSecondDash,
      groupPageLeft,
      groupStrikeLeft,
    };
  }, [individualPricingExclusions]);

  /** 관리자 등록 유료 이벤트 상품 → 카드 표시용(기간·정가 산출) */
  const paidEventProductCards = useMemo(() => {
    return paidEventProducts
      .map((product) => {
        const period = resolvePaidEventProductPeriod(product);
        if (!period) return null;
        const listPricing = computePaidPlanPricing(
          period.start,
          period.start,
          PAID_PLAN_MONTHLY_WON,
          PAID_PLAN_PARTIAL_DAY_RATE_WON,
          period.end,
          period.start,
        );
        const strike =
          product.flatAmountWon > 0 &&
          listPricing.totalWon > 0 &&
          product.flatAmountWon < listPricing.totalWon;
        return {
          product,
          periodStart: period.start,
          periodEnd: period.end,
          listWon: listPricing.totalWon,
          strike,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [paidEventProducts]);

  const planMidFreeRef = useRef<HTMLDivElement | null>(null);
  const planMidPaidEventRef = useRef<HTMLDivElement | null>(null);
  const planMidLeftRef = useRef<HTMLDivElement | null>(null);
  const planMidRightRef = useRef<HTMLDivElement | null>(null);
  const [planMidSlotPx, setPlanMidSlotPx] = useState(0);

  useLayoutEffect(() => {
    const els = [
      planMidFreeRef.current,
      planMidPaidEventRef.current,
      planMidLeftRef.current,
      planMidRightRef.current,
    ];
    const contentHeight = (el: HTMLDivElement | null) => {
      if (!el) return 0;
      const prev = el.style.minHeight;
      el.style.minHeight = '0px';
      const h = el.scrollHeight;
      el.style.minHeight = prev;
      return h;
    };
    const measure = () => {
      const h = Math.max(0, ...els.map(contentHeight));
      // 비정상 팽창 방지(이전 flex/% 높이 버그 잔존값 차단)
      const next = h > 0 ? Math.min(Math.ceil(h), 160) : 0;
      setPlanMidSlotPx((prev) => (prev === next ? prev : next));
    };
    measure();
    const RO = typeof ResizeObserver !== 'undefined' ? ResizeObserver : null;
    const ro = RO ? new RO(() => measure()) : null;
    if (ro) {
      for (const el of els) {
        if (el) ro.observe(el);
      }
    }
    return () => ro?.disconnect();
  }, [passPlanUi, paidEventProductCards.length, paidCheckoutEnabled]);

  const activeIndividualPasses = useMemo(
    () =>
      individualHistory.filter(
        (r) => !r.refundedAt && isInclusiveDateRangeActive(r.periodStart, r.periodEnd)
      ),
    [individualHistory]
  );

  const upcomingIndividualPasses = useMemo(
    () =>
      individualHistory.filter(
        (r) => !r.refundedAt && isUpcomingPaidPassWindow(r.periodStart, r.periodEnd)
      ),
    [individualHistory]
  );

  const mergedIndividualCollapsed = useMemo(() => {
    const m = new Map<string, IndividualPassPurchaseRecord>();
    for (const r of activeIndividualPasses) m.set(r.id, r);
    for (const r of upcomingIndividualPasses) m.set(r.id, r);
    const out: IndividualPassPurchaseRecord[] = [];
    m.forEach((r) => out.push(r));
    return out.sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
  }, [activeIndividualPasses, upcomingIndividualPasses]);

  const individualHistoryRowsVisible = useMemo(() => {
    if (individualHistoryDetailOpen) return individualHistory;
    return mergedIndividualCollapsed;
  }, [individualHistoryDetailOpen, individualHistory, mergedIndividualCollapsed]);

  const activeGroupPassesInWindow = useMemo(
    () =>
      groupHistory.filter(
        (g) =>
          !g.refundedAt &&
          isGroupPassRegisteredOnThisSession(g, sessionGroupPassBinding) &&
          isInclusiveDateRangeActive(g.activationPeriodStart, g.activationPeriodEnd)
      ),
    [groupHistory, sessionGroupPassBinding]
  );

  const upcomingGroupPasses = useMemo(
    () =>
      groupHistory.filter(
        (g) =>
          !g.refundedAt &&
          isGroupPassRegisteredOnThisSession(g, sessionGroupPassBinding) &&
          isUpcomingPaidPassWindow(g.activationPeriodStart, g.activationPeriodEnd)
      ),
    [groupHistory, sessionGroupPassBinding]
  );

  /** 구매했으나 이 세션에서 아직 쿠폰을 등록하지 않은 건(유효 기간 내) — 이력 접기 목록에 표시 */
  const groupPassesAwaitingCouponOnSession = useMemo(
    () =>
      groupHistory.filter(
        (g) =>
          !g.refundedAt &&
          !isGroupPassRegisteredOnThisSession(g, sessionGroupPassBinding) &&
          Date.now() <= endOfDayOnly(g.activationPeriodEnd).getTime()
      ),
    [groupHistory, sessionGroupPassBinding]
  );

  const mergedGroupCollapsed = useMemo(() => {
    const m = new Map<string, GroupPassPurchaseRecord>();
    for (const g of activeGroupPassesInWindow) m.set(g.id, g);
    for (const g of upcomingGroupPasses) m.set(g.id, g);
    for (const g of groupPassesAwaitingCouponOnSession) m.set(g.id, g);
    const out: GroupPassPurchaseRecord[] = [];
    m.forEach((g) => out.push(g));
    return out.sort(
      (a, b) => a.activationPeriodStart.getTime() - b.activationPeriodStart.getTime()
    );
  }, [activeGroupPassesInWindow, upcomingGroupPasses, groupPassesAwaitingCouponOnSession]);

  const groupHistoryRowsVisible = useMemo(() => {
    if (groupHistoryDetailOpen) return groupHistory;
    return mergedGroupCollapsed;
  }, [groupHistoryDetailOpen, groupHistory, mergedGroupCollapsed]);

  const activeEventWindow = useMemo(() => {
    if (!eventPassPeriod) return null;
    if (!isInclusiveDateRangeActive(eventPassPeriod.periodStart, eventPassPeriod.periodEnd)) return null;
    return eventPassPeriod;
  }, [eventPassPeriod]);

  /**
   * 이용권 중복 적용 금지: 지금 받을 이용권과 같은 학년도를 덮는 이용 중 이용권이 있으면 등록·구매 불가.
   * 이용권 연도가 2월에 한 달 겹치므로, 2월에는 기존 이용권이 남아 있어도 다음 학년도 이용권을 받을 수 있다.
   * (코드 이용권은 서버가 코드의 실제 기간으로 다시 판정)
   */
  const purchaseYear = passYearForPurchase();
  const hasActivePass =
    activeIndividualPasses.some((r) => passYearOfEnd(r.periodEnd) === purchaseYear) ||
    activeGroupPassesInWindow.some((g) => passYearOfEnd(g.activationPeriodEnd) === purchaseYear) ||
    (activeEventWindow != null && passYearOfEnd(activeEventWindow.periodEnd) === purchaseYear);

  const assertNoActivePass = useCallback(
    (passLabel: string): boolean => {
      if (!hasActivePass) return true;
      window.alert(activePassBlockedMessage(passLabel));
      return false;
    },
    [hasActivePass],
  );

  const calendarActivePlanTiles = useMemo((): PlanStripTile[] => {
    const tiles: PlanStripTile[] = [];
    for (const r of activeIndividualPasses) {
      tiles.push({
        key: `ind-${r.id}`,
        kindLabel: '개별 이용권',
        schoolYearTitle: schoolYearPassTitleFromStart(r.periodStart),
        periodLine: `${formatDateDot(r.periodStart)} ~ ${formatDateDot(r.periodEnd)}`,
        rangeStart: r.periodStart,
        rangeEnd: r.periodEnd,
        phase: 'calendar-active',
      });
    }
    for (const g of activeGroupPassesInWindow) {
      tiles.push({
        key: `grp-${g.id}`,
        kindLabel: '그룹 이용권',
        schoolYearTitle: schoolYearPassTitleFromStart(g.activationPeriodStart),
        periodLine: `${formatDateDot(g.activationPeriodStart)} ~ ${formatDateDot(g.activationPeriodEnd)}`,
        rangeStart: g.activationPeriodStart,
        rangeEnd: g.activationPeriodEnd,
        phase: 'calendar-active',
        groupPurchaserAccountLabel: groupPassPurchaserAccountLabel(g),
        groupCouponCode: g.couponCode,
      });
    }
    for (const e of eventHistory) {
      if (e.refundedAt || !isInclusiveDateRangeActive(e.periodStart, e.periodEnd)) continue;
      tiles.push({
        key: `evt-${e.id}`,
        kindLabel: eventPassKindLabel(e),
        schoolYearTitle: schoolYearPassTitleFromStart(e.periodStart),
        periodLine: `${formatDateDot(e.periodStart)} ~ ${formatDateDot(e.periodEnd)}`,
        rangeStart: e.periodStart,
        rangeEnd: e.periodEnd,
        phase: 'calendar-active',
      });
    }
    return tiles;
  }, [activeIndividualPasses, activeGroupPassesInWindow, eventHistory]);

  const upcomingPlanTiles = useMemo((): PlanStripTile[] => {
    const tiles: PlanStripTile[] = [];
    for (const r of upcomingIndividualPasses) {
      tiles.push({
        key: `ind-up-${r.id}`,
        kindLabel: '개별 이용권',
        schoolYearTitle: schoolYearPassTitleFromStart(r.periodStart),
        periodLine: `${formatDateDot(r.periodStart)} ~ ${formatDateDot(r.periodEnd)}`,
        rangeStart: r.periodStart,
        rangeEnd: r.periodEnd,
        phase: 'upcoming',
      });
    }
    for (const g of upcomingGroupPasses) {
      tiles.push({
        key: `grp-up-${g.id}`,
        kindLabel: '그룹 이용권',
        schoolYearTitle: schoolYearPassTitleFromStart(g.activationPeriodStart),
        periodLine: `${formatDateDot(g.activationPeriodStart)} ~ ${formatDateDot(g.activationPeriodEnd)}`,
        rangeStart: g.activationPeriodStart,
        rangeEnd: g.activationPeriodEnd,
        phase: 'upcoming',
        groupPurchaserAccountLabel: groupPassPurchaserAccountLabel(g),
        groupCouponCode: g.couponCode,
      });
    }
    return tiles;
  }, [upcomingIndividualPasses, upcomingGroupPasses]);

  const stripPlanTiles = useMemo(
    () => [...calendarActivePlanTiles, ...upcomingPlanTiles],
    [calendarActivePlanTiles, upcomingPlanTiles]
  );
  /** 오늘 날짜가 실제 청구·혜택 구간 안(유료 혜택 적용 중) — 테스트 계정 pro 메타 포함 */
  const calendarPaidActive = calendarActivePlanTiles.length > 0 || metaPlanPro;
  const hasStripPlanTiles = stripPlanTiles.length > 0 || metaPlanPro;
  const purchaseUiLocked = !paidCheckoutEnabled;

  /**
   * 카드별「이용중」·비활성:
   * - 상단 리본 없이 가격 아래 뱃지에「이용중」표시
   * - 프리 외 이용권이 적용 중이면 그룹 구매를 제외한 나머지 카드 비활성
   */
  const planCardInUse = useMemo(() => {
    if (!passHydrated && !isLandingPurchase) {
      return {
        freeInUse: false,
        suppressOtherPasses: false,
        leftInUse: false,
        rightInUse: false,
        leftEventInUse: false,
        leftInUseCoverageNote: false,
        rightInUseCoverageNote: false,
        leftGroupHint: false,
        rightGroupHint: false,
      };
    }
    const t = new Date();
    const leftW = passPlanUi.leftW;
    const rightW = passPlanUi.rightW;
    const coversTodayInWindow = (start: Date, end: Date, w: { start: Date; end: Date }) =>
      isInclusiveDateRangeActive(start, end) && isDateOnlyInInclusiveRange(t, w.start, w.end);

    const indLeft = activeIndividualPasses.some((r) =>
      coversTodayInWindow(r.periodStart, r.periodEnd, leftW),
    );
    const grpLeft = activeGroupPassesInWindow.some((g) =>
      coversTodayInWindow(g.activationPeriodStart, g.activationPeriodEnd, leftW),
    );
    const paidEvtLeft = eventHistory.some(
      (e) =>
        e.kind === 'paid' &&
        !e.refundedAt &&
        coversTodayInWindow(e.periodStart, e.periodEnd, leftW),
    );
    const indRight = activeIndividualPasses.some((r) =>
      coversTodayInWindow(r.periodStart, r.periodEnd, rightW),
    );
    const grpRight = activeGroupPassesInWindow.some((g) =>
      coversTodayInWindow(g.activationPeriodStart, g.activationPeriodEnd, rightW),
    );
    const evtRight = eventHistory.some(
      (e) => !e.refundedAt && coversTodayInWindow(e.periodStart, e.periodEnd, rightW),
    );

    const metaOnly = metaPlanPro && calendarActivePlanTiles.length === 0;
    /**
     * 학년도 유료 카드「이용중」:
     * 개별·그룹뿐 아니라 유료 이벤트(카드가 없거나 사라진 경우 포함)도 이 카드에 모아 표시.
     */
    const leftYearInUse =
      indLeft || grpLeft || paidEvtLeft || (metaOnly && passPlanUi.todayInLeft);
    const rightYearInUse =
      indRight ||
      grpRight ||
      evtRight ||
      (metaOnly && passPlanUi.todayInRight && !leftYearInUse);
    const hasNonFreeActive = leftYearInUse || rightYearInUse;
    const freeInUse = !hasNonFreeActive;

    return {
      freeInUse,
      /** 프리 외 적용 중 → 그룹 제외 나머지 카드 비활성 */
      suppressOtherPasses: hasNonFreeActive,
      leftInUse: leftYearInUse,
      rightInUse: rightYearInUse,
      /** 이벤트 전용 카드에는「이용중」을 두지 않음(학년도 카드로 통합) */
      leftEventInUse: false,
      /** 이용중 학년도 카드 하단 안내(이벤트·일반 유료 통합 설명) */
      leftInUseCoverageNote: leftYearInUse,
      rightInUseCoverageNote: rightYearInUse,
      leftGroupHint: grpLeft,
      rightGroupHint: grpRight,
    };
  }, [
    eventHistory,
    activeGroupPassesInWindow,
    activeIndividualPasses,
    calendarActivePlanTiles.length,
    metaPlanPro,
    passPlanUi.leftW,
    passPlanUi.rightW,
    passPlanUi.todayInLeft,
    passPlanUi.todayInRight,
    passHydrated,
    isLandingPurchase,
  ]);
  /** DB 저장: 하이드레이션 직후 1회는 스킵, 이후 변경은 짧게 디바운스 */
  const skipPassPersistOnceRef = useRef(true);
  /** 쿠폰 등록 RPC 가 서버에 이력을 기록한 뒤, 서버 상태로 화면을 다시 맞춘다 */
  const reloadPassStateFromServer = useCallback(async () => {
    if (!sessionUserId) return;
    const result = await syncMySubscriptionPassState(sessionUserId);
    skipPassPersistOnceRef.current = true;
    setIndividualHistory(result.state.individualHistory);
    setGroupHistory(result.state.groupHistory);
    setEventHistory(result.state.eventHistory);
    setEventPassPeriod(result.state.eventPassPeriod);
    setSessionGroupPassBinding(result.state.sessionGroupPassBinding);
  }, [sessionUserId]);
  useEffect(() => {
    if (!passHydrated) return;
    if (skipPassPersistOnceRef.current) {
      skipPassPersistOnceRef.current = false;
      return;
    }
    const nextState = {
      individualHistory,
      groupHistory,
      eventHistory,
      eventPassPeriod: deriveActiveEventPassPeriod(eventHistory) ?? eventPassPeriod,
      sessionGroupPassBinding,
    };
    if (!sessionUserId) {
      saveSubscriptionPassState(nextState, null);
      return;
    }
    const timer = window.setTimeout(() => {
      void persistMySubscriptionPassState(sessionUserId, nextState).catch((err) => {
        console.error(err);
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [
    eventHistory,
    eventPassPeriod,
    groupHistory,
    individualHistory,
    passHydrated,
    sessionGroupPassBinding,
    sessionUserId,
  ]);

  const assertPaidCheckoutAllowed = useCallback((): boolean => {
    if (!paidCheckoutEnabled) {
      window.alert(
        '현재 유료 결제가 잠겨 있습니다(베타 테스트).\n쿠폰 코드 등록은 이용할 수 있습니다.',
      );
      return false;
    }
    if (paymentMode === 'production') {
      window.alert(
        '실제 결제 모드입니다.\n실 PG(포트원 등) 연동 전까지 카드 결제를 진행할 수 없습니다.\n관리자 「결제 옵션 설정」에서 테스트 모드로 바꿔 주세요.',
      );
      return false;
    }
    if (!isPlatformAdminEmail(sessionEmail ?? '')) {
      window.alert(
        '테스트 결제는 관리자 계정에서만 사용할 수 있습니다.\n쿠폰 코드 등록은 이용할 수 있습니다.',
      );
      return false;
    }
    return true;
  }, [paidCheckoutEnabled, paymentMode, sessionEmail]);

  const openBetaCodeRequestModal = useCallback(() => {
    if (!sessionUserId) {
      promptLoginForCheckout();
      return;
    }
    setBetaCodeRequestError(null);
    setBetaCodeRequestForm({
      fullName: '',
      email: '',
      organization: '',
      note: '',
      agreed: false,
    });
    setBetaCodeRequestOpen(true);
  }, [promptLoginForCheckout, sessionUserId]);

  const betaCodeRequestCanSubmit =
    betaCodeRequestForm.fullName.trim().length > 0 &&
    simpleEmailValid(betaCodeRequestForm.email.trim()) &&
    betaCodeRequestForm.organization.trim().length > 0 &&
    betaCodeRequestForm.agreed &&
    !betaCodeRequestSubmitting;

  const submitBetaCodeRequestModal = useCallback(async () => {
    if (betaCodeRequestSubmitting) return;
    const fullName = betaCodeRequestForm.fullName.trim();
    const email = betaCodeRequestForm.email.trim();
    const organization = betaCodeRequestForm.organization.trim();
    const note = betaCodeRequestForm.note;
    if (!fullName) {
      setBetaCodeRequestError('이름을 입력해 주세요.');
      return;
    }
    if (!simpleEmailValid(email)) {
      setBetaCodeRequestError('올바른 이메일을 입력해 주세요.');
      return;
    }
    if (!organization) {
      setBetaCodeRequestError('소속을 입력해 주세요.');
      return;
    }
    if (!betaCodeRequestForm.agreed) {
      setBetaCodeRequestError('확인 사항에 동의해 주세요.');
      return;
    }
    if (!sessionUserId) {
      setBetaCodeRequestError('로그인한 뒤 요청할 수 있습니다.');
      return;
    }
    try {
      setBetaCodeRequestSubmitting(true);
      setBetaCodeRequestError(null);
      const payload = {
        fullName,
        email,
        organization,
        note,
        userId: sessionUserId,
      };
      // DB 접수 후 관리자 알림 메일·(자동발송 시) 이용자 코드 메일 — 서버가 요청 행 기준으로 발송
      const saved = await submitBetaEventCodeRequest(payload);
      if (!saved.ok) {
        console.error('beta request failed', saved);
        setBetaCodeRequestError(
          saved.alreadyRequested
            ? '이미 이벤트 코드를 요청하셨습니다. 계정당 한 번만 요청할 수 있습니다.'
            : saved.notAuthenticated
              ? '로그인한 뒤 요청할 수 있습니다.'
              : '요청을 보내지 못했습니다. 잠시 후 다시 시도하거나 관리자에게 문의해 주세요.',
        );
        return;
      }
      const mailTasks: Array<
        Promise<{ ok: boolean; skipped?: boolean; error?: string }>
      > = [sendBetaEventCodeRequestEmail({ requestId: saved.id })];
      if (saved.issued && saved.issuedCode) {
        mailTasks.push(sendBetaEventCodeIssueEmail({ requestId: saved.id }));
      }
      const mailResults = await Promise.all(mailTasks);
      const mail = mailResults[0]!;
      const issueMail = mailResults[1] ?? null;

      if (saved.issued) {
        setBetaCodeRequestOpen(false);
        if (issueMail && !issueMail.ok) {
          showInfo(
            '요청이 접수·코드가 배정되었습니다. 코드 안내 메일은 잠시 후 관리자 목록에서 확인해 주세요.',
          );
        } else {
          showInfo('이벤트 코드가 이메일로 발송되었습니다. 메일함을 확인해 주세요.');
        }
        return;
      }

      setBetaCodeRequestOpen(false);
      showInfo(
        mail.ok
          ? '이벤트 코드 요청이 접수되었습니다. 확인 후 안내 드리겠습니다.'
          : '요청이 접수되었습니다. 메일 발송은 잠시 후 관리자 목록에서 확인해 주세요.',
      );
    } catch (err) {
      console.error(err);
      setBetaCodeRequestError(
        '요청을 보내지 못했습니다. 잠시 후 다시 시도하거나 관리자에게 문의해 주세요.',
      );
    } finally {
      setBetaCodeRequestSubmitting(false);
    }
  }, [
    betaCodeRequestForm.agreed,
    betaCodeRequestForm.email,
    betaCodeRequestForm.fullName,
    betaCodeRequestForm.note,
    betaCodeRequestForm.organization,
    betaCodeRequestSubmitting,
    sessionUserId,
    showInfo,
  ]);

  useEffect(() => {
    if (!sessionGroupPassBinding) return;
    const g = groupHistory.find((x) => x.id === sessionGroupPassBinding.purchaseRecordId);
    if (!g || g.refundedAt) {
      setSessionGroupPassBinding(null);
      return;
    }
    if (Date.now() > endOfDayOnly(g.activationPeriodEnd).getTime()) {
      setSessionGroupPassBinding(null);
    }
  }, [groupHistory, sessionGroupPassBinding]);

  const individualGrossPaid = useMemo(
    () => individualHistory.reduce((s, r) => s + r.amountWon, 0),
    [individualHistory]
  );
  const individualRefundedSum = useMemo(
    () => individualHistory.reduce((s, r) => s + (r.refundAmountWon ?? 0), 0),
    [individualHistory]
  );
  const individualPurchasedCount = individualHistory.length;
  const individualRefundedCount = useMemo(
    () => individualHistory.filter((r) => Boolean(r.refundedAt)).length,
    [individualHistory]
  );

  const groupGrossPaid = useMemo(
    () => groupHistory.reduce((s, r) => s + r.totalWon, 0),
    [groupHistory]
  );
  const groupRefundedSum = useMemo(
    () => groupHistory.reduce((s, r) => s + (r.refundAmountWon ?? 0), 0),
    [groupHistory]
  );
  const groupPurchasedCount = useMemo(
    () => groupHistory.reduce((s, g) => s + (Number(g.quantity) || 0), 0),
    [groupHistory]
  );
  const groupRefundedCount = useMemo(
    () =>
      groupHistory.reduce(
        (s, g) => s + (g.refundedAt ? Number(g.quantity) || 0 : 0),
        0
      ),
    [groupHistory]
  );

  const refundPreview: PassRefundPreview | null = useMemo(() => {
    if (!refundModal) return null;
    if (refundModal.kind === 'individual') {
      const r = individualHistory.find((x) => x.id === refundModal.recordId);
      if (!r) return null;
      const peers = individualHistory.filter((x) => !x.refundedAt);
      return computeIndividualPassRefundPreview(r, new Date(), peers);
    }
    if (refundModal.kind === 'event') {
      const e = eventHistory.find((x) => x.id === refundModal.recordId);
      return e ? computeEventPassRefundPreview(e) : null;
    }
    const g = groupHistory.find((x) => x.id === refundModal.recordId);
    return g ? computeGroupPassRefundPreview(g) : null;
  }, [refundModal, individualHistory, groupHistory, eventHistory]);

  /** 그룹 이용권: 카드에 표시된 학년도(개별 이용권과 동일 구간) 종료일 */
  const groupPassSchoolYearEnd = passPlanUi.leftW.end;

  const updateGroupBillingStartIso = useCallback(
    (iso: string) => {
      const purchase = new Date();
      const { minIso, maxIso } = getGroupPassBillingDatePickBounds(purchase, groupPassSchoolYearEnd);
      let v = iso.trim();
      if (v < minIso) v = minIso;
      if (v > maxIso) v = maxIso;
      const chosen = parseLocalIsoDateOnly(v);
      if (!chosen) return;
      setGroupForm((f) => ({ ...f, billingStartIso: v }));
      setGroupBillingStartDraft(formatGroupBillingStartDraft(v));
      setModal((prev) =>
        prev?.kind === 'group'
          ? {
              ...prev,
              basePricing: computePaidPlanPricing(
                new Date(chosen.getFullYear(), chosen.getMonth(), 1),
                purchase,
                PAID_PLAN_MONTHLY_WON,
                PAID_PLAN_PARTIAL_DAY_RATE_WON,
                groupPassSchoolYearEnd,
                chosen
              ),
            }
          : prev
      );
    },
    [groupPassSchoolYearEnd]
  );

  const onGroupBillingStartDraftChange = useCallback(
    (raw: string) => {
      setGroupBillingStartDraft(raw);
      const parsed = parseGroupBillingStartDraft(raw);
      if (!parsed) return;
      const { minIso, maxIso } = getGroupPassBillingDatePickBounds(
        new Date(),
        groupPassSchoolYearEnd
      );
      if (parsed < minIso) {
        updateGroupBillingStartIso(minIso);
        return;
      }
      if (parsed > maxIso) {
        updateGroupBillingStartIso(maxIso);
        return;
      }
      updateGroupBillingStartIso(parsed);
    },
    [groupPassSchoolYearEnd, updateGroupBillingStartIso]
  );

  const commitGroupBillingStartDraft = useCallback(() => {
    const { minIso } = getGroupPassBillingDatePickBounds(new Date(), groupPassSchoolYearEnd);
    const parsed = parseGroupBillingStartDraft(groupBillingStartDraft);
    updateGroupBillingStartIso(parsed && parsed >= minIso ? parsed : minIso);
  }, [groupBillingStartDraft, groupPassSchoolYearEnd, updateGroupBillingStartIso]);

  const groupBillingDateBounds = useMemo(
    () =>
      modal?.kind === 'group'
        ? getGroupPassBillingDatePickBounds(new Date(), groupPassSchoolYearEnd)
        : null,
    [modal, groupPassSchoolYearEnd]
  );
  const closeModal = useCallback(() => setModal(null), []);

  const copyGroupHistoryCouponCode = useCallback(async (purchaseRecordId: string, code: string) => {
    const c = String(code ?? '').trim();
    if (!c) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(c);
      copied = true;
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = c;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        copied = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        copied = false;
      }
    }
    if (!copied) return;
    setGroupHistoryCouponCopiedId(purchaseRecordId);
    window.setTimeout(() => {
      setGroupHistoryCouponCopiedId((cur) => (cur === purchaseRecordId ? null : cur));
    }, 2000);
  }, []);

  useEffect(() => {
    if (!individualReceipt) return;
    void (async () => {
      try {
        const session = await getSession();
        const u = session?.user;
        if (!u) {
          setReceiptBuyerName('구매자');
          setReceiptBuyerEmail('—');
          return;
        }
        const meta = u.user_metadata as Record<string, unknown> | undefined;
        const name =
          (typeof meta?.full_name === 'string' && meta.full_name) ||
          (typeof meta?.name === 'string' && meta.name) ||
          u.email?.split('@')[0] ||
          '구매자';
        setReceiptBuyerName(name);
        setReceiptBuyerEmail(u.email ?? '—');
      } catch {
        setReceiptBuyerName('구매자');
        setReceiptBuyerEmail('—');
      }
    })();
  }, [individualReceipt]);

  const openIndividualModalForSchoolYear = useCallback(
    (schoolYearLabel: number) => {
      if (!assertPaidCheckoutAllowed()) return;
      if (!assertNoActivePass('개별 이용권')) return;
      const t = new Date();
      if (
        startOfDayOnly(t).getTime() <
        startOfDayOnly(getSchoolYearPassPurchaseOpensAt(schoolYearLabel)).getTime()
      ) {
        window.alert(`해당 이용권은 ${schoolYearLabel}년 1월부터 구매할 수 있습니다.`);
        return;
      }
      const { start: ws, end: we } = getSchoolYearPassLicenseWindow(schoolYearLabel);
      const months = listBillingStartMonthsInLicenseWindow(t, ws, we);
      let picked: PaidPlanPricingResult | null = null;
      for (const m of months) {
        const p = computePaidPlanPricingExcludingIndividualCoverage(
          m,
          t,
          individualPricingExclusions,
          PAID_PLAN_MONTHLY_WON,
          PAID_PLAN_PARTIAL_DAY_RATE_WON,
          we
        );
        if (p.totalWon > 0) {
          picked = p;
          break;
        }
      }
      if (!picked) {
        window.alert(
          '추가로 구매할 수 있는 이용 기간이 없습니다.\n' +
            `«${schoolYearLabel}학년도» 이용권 구간(2월~다음 해 2월 말)에서는 이미 개별·그룹·이벤트 이용권으로 적용할 수 있는 날짜가 모두 포함된 상태입니다.`
        );
        return;
      }
      setIndividualAgreeRefund(false);
      setModal({ kind: 'individual', pricing: picked });
    },
    [assertPaidCheckoutAllowed, individualPricingExclusions, assertNoActivePass]
  );

  const openPaidEventProductPurchase = useCallback(
    (product: AdminPaidEventPassProduct) => {
      if (!assertPaidCheckoutAllowed()) return;
      if (!assertNoActivePass('이벤트 이용권')) return;
      if (isPaidEventProductSoldOut(product)) {
        window.alert('구매 가능 인원이 모두 소진되어 더 이상 구매할 수 없습니다.');
        reloadPaidEventProducts();
        return;
      }
      const period = resolvePaidEventProductPeriod(product);
      if (!period) {
        window.alert('이용권 기간 정보가 올바르지 않습니다. 관리자에게 문의해 주세요.');
        return;
      }
      setIndividualAgreeRefund(false);
      setModal({
        kind: 'event',
        productId: product.id,
        title: product.eventName,
        periodStart: period.start,
        periodEnd: period.end,
        totalWon: product.flatAmountWon,
        durationMonths: product.durationMonths,
      });
    },
    [assertPaidCheckoutAllowed, reloadPaidEventProducts, assertNoActivePass],
  );

  const executeEventPurchase = useCallback(async () => {
    if (modal?.kind !== 'event') return;
    if (!assertNoActivePass('이벤트 이용권')) return;
    try {
      await incrementPaidEventProductSoldRemote(modal.productId);
    } catch (err) {
      console.error(err);
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : '';
      window.alert(
        /SOLD_OUT/i.test(msg)
          ? '구매 가능 인원이 모두 소진되어 더 이상 구매할 수 없습니다.'
          : '이용권 구매 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.',
      );
      reloadPaidEventProducts();
      closeModal();
      return;
    }
    const now = new Date();
    const row: EventPassPurchaseRecord = {
      id: `evt-paid-${Date.now()}`,
      kind: 'paid',
      title: modal.title,
      purchasedAt: now,
      periodStart: modal.periodStart,
      periodEnd: modal.periodEnd,
      amountWon: modal.totalWon,
      refundDisabled: false,
    };
    setEventHistory((prev) => [row, ...prev]);
    setEventPassPeriod({ periodStart: modal.periodStart, periodEnd: modal.periodEnd });
    reloadPaidEventProducts();
    showInfo(
      `「${modal.title}」유료 이벤트 이용권이 적용되었습니다. (일괄 ${formatKrw(modal.totalWon)}원)`,
    );
    closeModal();
  }, [modal, closeModal, showInfo, reloadPaidEventProducts, assertNoActivePass]);

  const confirmEventPurchase = useCallback(() => {
    if (modal?.kind !== 'event') return;
    if (!individualAgreeRefund) {
      window.alert('환불 정책 안내를 확인하고 동의해 주세요.');
      return;
    }
    if (requireLoginForCheckout) {
      promptLoginForCheckout();
      return;
    }
    if (!assertPaidCheckoutAllowed()) return;
    if (paymentMode === 'test') {
      setTestPayPending('event');
      setTestPayPassword('');
      setTestPayError(null);
      return;
    }
    void executeEventPurchase();
  }, [
    individualAgreeRefund,
    assertPaidCheckoutAllowed,
    executeEventPurchase,
    modal,
    paymentMode,
    promptLoginForCheckout,
    requireLoginForCheckout,
  ]);


  const openGroupModal = useCallback(() => {
    if (!assertPaidCheckoutAllowed()) return;
    const t = new Date();
    const endCap = passPlanUi.leftW.end;
    const { minIso, maxIso } = getGroupPassBillingDatePickBounds(t, endCap);
    let iso = toLocalIsoDateOnly(t);
    if (iso < minIso) iso = minIso;
    if (iso > maxIso) iso = maxIso;
    const purchaseDay = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 0, 0, 0, 0);
    const chosen = parseLocalIsoDateOnly(iso) ?? purchaseDay;
    const monthFirst = new Date(chosen.getFullYear(), chosen.getMonth(), 1);
    setGroupForm({
      groupName: '',
      email: '',
      quantity: 1,
      agreeActivation: false,
      agreeRefund: false,
      billingStartIso: iso,
    });
    setGroupBillingStartDraft(formatGroupBillingStartDraft(iso));
    setModal({
      kind: 'group',
      basePricing: computePaidPlanPricing(
        monthFirst,
        t,
        PAID_PLAN_MONTHLY_WON,
        PAID_PLAN_PARTIAL_DAY_RATE_WON,
        endCap,
        chosen
      ),
    });
  }, [assertPaidCheckoutAllowed, passPlanUi.leftW.end]);

  const closeTestPayModal = useCallback(() => {
    setTestPayPending(null);
    setTestPayPassword('');
    setTestPayError(null);
  }, []);

  const executeIndividualPurchase = useCallback(() => {
    if (modal?.kind !== 'individual') return;
    if (!assertNoActivePass('개별 이용권')) return;
    const p = modal.pricing;
    if (p.totalWon <= 0) {
      window.alert(
        '추가로 구매할 수 있는 이용 일수가 없습니다.\n' +
          '현재 이용권 기간(매년 2월~다음 해 2월 말) 안에서는 이미 개별·그룹·이벤트 이용권으로 모두 적용된 상태입니다.'
      );
      return;
    }
    const id = `ind-${Date.now()}`;
    setIndividualHistory((prev) => [
      {
        id,
        purchasedAt: new Date(p.purchaseDate),
        periodStart: new Date(p.periodStart),
        periodEnd: new Date(p.billedPeriodEndInclusive),
        amountWon: p.totalWon,
      },
      ...prev,
    ]);
    window.alert('결제·이용권 반영은 추후 연동됩니다. (데모: 이력에 반영되었습니다.)');
    closeModal();
  }, [modal, closeModal, assertNoActivePass]);

  const confirmIndividualPurchase = useCallback(() => {
    if (modal?.kind !== 'individual') return;
    if (!individualAgreeRefund) {
      window.alert('환불 정책 안내를 확인하고 동의해 주세요.');
      return;
    }
    if (requireLoginForCheckout) {
      savePendingIndividualCheckout(modal.pricing);
      promptLoginForCheckout();
      return;
    }
    if (!assertPaidCheckoutAllowed()) return;
    const p = modal.pricing;
    if (p.totalWon <= 0) {
      window.alert(
        '추가로 구매할 수 있는 이용 일수가 없습니다.\n' +
          '현재 이용권 기간(매년 2월~다음 해 2월 말) 안에서는 이미 개별·그룹·이벤트 이용권으로 모두 적용된 상태입니다.'
      );
      return;
    }
    if (paymentMode === 'test') {
      setTestPayPending('individual');
      setTestPayPassword('');
      setTestPayError(null);
      return;
    }
    executeIndividualPurchase();
  }, [
    individualAgreeRefund,
    assertPaidCheckoutAllowed,
    executeIndividualPurchase,
    modal,
    paymentMode,
    promptLoginForCheckout,
    requireLoginForCheckout,
  ]);

  const groupTotalWon =
    modal?.kind === 'group' ? modal.basePricing.totalWon * groupForm.quantity : 0;
  const groupPayEnabled =
    modal?.kind === 'group' &&
    modal.basePricing.totalWon > 0 &&
    groupForm.groupName.trim().length > 0 &&
    simpleEmailValid(groupForm.email) &&
    groupForm.quantity >= 1 &&
    groupForm.agreeActivation &&
    groupForm.agreeRefund;

  const openRefundModal = useCallback(
    (kind: 'individual' | 'group' | 'event', recordId: string) => {
      if (kind === 'event') {
        const row = eventHistory.find((x) => x.id === recordId);
        if (!row) return;
        const preview = computeEventPassRefundPreview(row);
        if (!preview.eligible) {
          window.alert(preview.reason || '지금은 환불을 진행할 수 없습니다.');
          return;
        }
        setRefundModal({ kind, recordId, step: 1, agreed: false });
        return;
      }
      const row =
        kind === 'individual'
          ? individualHistory.find((x) => x.id === recordId)
          : groupHistory.find((x) => x.id === recordId);
      if (!row) return;
      const peers = individualHistory.filter((x) => !x.refundedAt);
      const preview =
        kind === 'individual'
          ? computeIndividualPassRefundPreview(row as IndividualPassPurchaseRecord, new Date(), peers)
          : computeGroupPassRefundPreview(row as GroupPassPurchaseRecord);
      if (!preview.eligible) {
        window.alert(preview.reason || '지금은 환불을 진행할 수 없습니다.');
        return;
      }
      setRefundModal({ kind, recordId, step: 1, agreed: false });
    },
    [individualHistory, groupHistory, eventHistory]
  );

  const completeRefund = useCallback(() => {
    if (!refundModal || refundModal.step !== 2) return;
    const now = new Date();
    if (refundModal.kind === 'individual') {
      const row = individualHistory.find((x) => x.id === refundModal.recordId);
      if (!row || row.refundedAt) return;
      const p = computeIndividualPassRefundPreview(
        row,
        now,
        individualHistory.filter((x) => !x.refundedAt)
      );
      if (!p.eligible || p.refundWon <= 0) return;
      setIndividualHistory((prev) =>
        prev.map((r) =>
          r.id === refundModal.recordId
            ? { ...r, refundedAt: now, refundAmountWon: p.refundWon }
            : r
        )
      );
      setRefundModal((m) =>
        m ? { ...m, step: 3, completedRefundWon: p.refundWon } : null
      );
      return;
    }
    if (refundModal.kind === 'event') {
      const row = eventHistory.find((x) => x.id === refundModal.recordId);
      if (!row || row.refundedAt) return;
      const p = computeEventPassRefundPreview(row, now);
      if (!p.eligible || p.refundWon <= 0) return;
      const updated = eventHistory.map((r) =>
        r.id === refundModal.recordId
          ? { ...r, refundedAt: now, refundAmountWon: p.refundWon }
          : r
      );
      setEventHistory(updated);
      setEventPassPeriod(deriveActiveEventPassPeriod(updated));
      setRefundModal((m) => (m ? { ...m, step: 3, completedRefundWon: p.refundWon } : null));
      return;
    }
    const row = groupHistory.find((x) => x.id === refundModal.recordId);
    if (!row || row.refundedAt) return;
    const p = computeGroupPassRefundPreview(row, now);
    if (!p.eligible || p.refundWon <= 0) return;
    setGroupHistory((prev) =>
      prev.map((r) =>
        r.id === refundModal.recordId ? { ...r, refundedAt: now, refundAmountWon: p.refundWon } : r
      )
    );
    setSessionGroupPassBinding((b) => (b?.purchaseRecordId === refundModal.recordId ? null : b));
    setRefundModal((m) => (m ? { ...m, step: 3, completedRefundWon: p.refundWon } : null));
  }, [refundModal, individualHistory, groupHistory, eventHistory]);

  const executeGroupPurchase = useCallback(async () => {
    if (modal?.kind !== 'group') return;
    const ok =
      groupForm.groupName.trim().length > 0 &&
      simpleEmailValid(groupForm.email) &&
      groupForm.quantity >= 1 &&
      groupForm.agreeActivation &&
      groupForm.agreeRefund;
    if (!ok) return;
    if (modal.basePricing.totalWon <= 0) {
      window.alert(
        '선택한 시작일 기준으로 납부할 이용 일수가 없습니다.\n시작일을 바꾸거나, 이용 구간을 확인해 주세요.'
      );
      return;
    }
    const session = await getSession();
    const purchaserAccountEmail = session?.user?.email?.trim() ?? '';
    const base = modal.basePricing;
    const purchasedAt = new Date(base.purchaseDate);
    const id = newAdminIssuedPassRowId();
    const couponCode = generateGroupCouponCode();
    const record: GroupPassPurchaseRecord = {
      id,
      purchasedAt,
      groupName: groupForm.groupName.trim(),
      couponEmail: groupForm.email.trim(),
      ...(purchaserAccountEmail ? { purchaserAccountEmail } : {}),
      quantity: groupForm.quantity,
      activatedCount: 0,
      unitProratedWon: base.totalWon,
      totalWon: base.totalWon * groupForm.quantity,
      couponCode,
      activationPeriodStart: new Date(base.periodStart),
      activationPeriodEnd: new Date(base.billedPeriodEndInclusive),
      refundPeriodStart: purchasedAt,
      refundPeriodEnd: addDaysStartOfDay(purchasedAt, 6),
      issuedByAdmin: false,
    };
    setGroupHistory((prev) => [record, ...prev]);
    setGroupPurchaseSubmitting(true);
    try {
      await issueGroupPassCodeRemote({
        id,
        code: couponCode,
        groupName: record.groupName,
        contactEmail: record.couponEmail,
        paidPlanStartDate: toLocalIsoDateOnly(record.activationPeriodStart),
        userCount: record.quantity,
      });
    } catch (err) {
      console.error(err);
      window.alert(
        '그룹 이용권 코드를 서버에 저장하지 못했습니다. 같은 브라우저에서는 이용할 수 있으나, 다른 기기 등록을 위해 다시 시도하거나 관리자에게 문의해 주세요.',
      );
    }
    const mail = await sendGroupPassPurchaseEmail({
      to: record.couponEmail,
      groupName: record.groupName,
      couponCode: record.couponCode,
      quantity: record.quantity,
      activationPeriodStart: record.activationPeriodStart.toISOString(),
      activationPeriodEnd: record.activationPeriodEnd.toISOString(),
      totalWon: record.totalWon,
    });
    setGroupPurchaseSubmitting(false);
    const lines = [
      '그룹 이용권 쿠폰이 발급되었습니다.',
      `그룹 코드: ${record.couponCode}`,
      '',
      '이 코드를 「쿠폰 코드 등록」에 입력해야 현재 활성 플랜에 반영됩니다.',
    ];
    if (mail.ok) {
      lines.push('', `입력하신 이메일(${record.couponEmail})로 쿠폰 안내 메일을 발송했습니다.`);
    } else if (mail.skipped) {
      lines.push(
        '',
        '안내 메일은 SMTP(Gmail)가 설정된 환경에서만 발송됩니다. (지금은 발송을 건너뛰었을 수 있습니다.)',
        '설정 방법: docs/GROUP_PASS_EMAIL.md'
      );
    } else {
      lines.push('', `안내 메일 발송에 실패했습니다. 잠시 후 다시 시도하거나 관리자에게 문의해 주세요. (${mail.error})`);
    }
    window.alert(lines.join('\n'));
    closeModal();
  }, [modal, groupForm, closeModal]);

  const confirmGroupPurchase = useCallback(async () => {
    if (modal?.kind !== 'group') return;
    if (requireLoginForCheckout) {
      savePendingGroupCheckout(modal.basePricing, { ...groupForm });
      promptLoginForCheckout();
      return;
    }
    if (!assertPaidCheckoutAllowed()) return;
    const ok =
      groupForm.groupName.trim().length > 0 &&
      simpleEmailValid(groupForm.email) &&
      groupForm.quantity >= 1 &&
      groupForm.agreeActivation &&
      groupForm.agreeRefund;
    if (!ok) return;
    if (modal.basePricing.totalWon <= 0) {
      window.alert(
        '선택한 시작일 기준으로 납부할 이용 일수가 없습니다.\n시작일을 바꾸거나, 이용 구간을 확인해 주세요.'
      );
      return;
    }
    if (paymentMode === 'test') {
      setTestPayPending('group');
      setTestPayPassword('');
      setTestPayError(null);
      return;
    }
    await executeGroupPurchase();
  }, [
    assertPaidCheckoutAllowed,
    executeGroupPurchase,
    modal,
    groupForm,
    paymentMode,
    promptLoginForCheckout,
    requireLoginForCheckout,
  ]);

  const confirmTestPayPassword = useCallback(async () => {
    if (!testPayPending) return;
    if (groupPurchaseSubmitting) return;
    const expected = getPlatformTestPaymentPassword();
    if (testPayPassword !== expected) {
      setTestPayError('테스트 비밀번호가 올바르지 않습니다.');
      return;
    }
    const pending = testPayPending;
    closeTestPayModal();
    if (pending === 'individual') {
      executeIndividualPurchase();
      return;
    }
    if (pending === 'event') {
      void executeEventPurchase();
      return;
    }
    await executeGroupPurchase();
  }, [
    closeTestPayModal,
    executeEventPurchase,
    executeGroupPurchase,
    executeIndividualPurchase,
    groupPurchaseSubmitting,
    testPayPassword,
    testPayPending,
  ]);

  const registerGroupCouponCode = useCallback(async () => {
    const parsed = parseGroupCouponCodeInput(couponCodeInput);
    if (!parsed) {
      window.alert(
        `그룹 이용권 쿠폰 형식이 올바르지 않습니다.\n예: GRP- 로 시작하고 뒤에 ${PASS_CODE_BODY_LENGTH}자(영문·숫자, I/O/0/1 제외)입니다.`,
      );
      return;
    }
    if (!assertNoActivePass('그룹 이용권')) return;

    const applyLocalMatch = (localMatch: GroupPassPurchaseRecord) => {
      if (Date.now() > endOfDayOnly(localMatch.activationPeriodEnd).getTime()) {
        window.alert('이 쿠폰의 유효 기간(이용 종료일)이 지났습니다.');
        return;
      }
      if (localMatch.activatedCount >= localMatch.quantity) {
        window.alert('이 쿠폰으로 등록할 수 있는 이용권(계정) 수가 모두 사용되었습니다.');
        return;
      }
      const now = new Date();
      setGroupHistory((prev) =>
        prev.map((g) =>
          g.id === localMatch.id
            ? { ...g, activatedCount: Math.min(g.quantity, g.activatedCount + 1) }
            : g,
        ),
      );
      setSessionGroupPassBinding({ purchaseRecordId: localMatch.id, appliedAt: now });
      window.alert(
        '그룹 이용권 쿠폰이 등록되었습니다. 현재 계정의 유료 플랜·이용 기간에 반영되었습니다.',
      );
      setCouponCodeInput('');
      setCouponModalOpen(false);
    };

    try {
      const redeemed = await redeemGroupPassCodeRemote(parsed);
      const periodStart = parseLocalIsoDateOnly(String(redeemed.paid_plan_start_date).slice(0, 10));
      const periodEnd = parseLocalIsoDateOnly(String(redeemed.period_end_iso).slice(0, 10));
      if (!periodStart || !periodEnd) {
        window.alert('코드는 등록되었으나 이용 기간 정보가 올바르지 않습니다. 관리자에게 문의해 주세요.');
        return;
      }
      await reloadPassStateFromServer();
      window.alert(
        `그룹 이용권 쿠폰이 등록되었습니다.\n「${redeemed.group_name || '그룹 이용권'}」 / ${formatDateDot(periodStart)} ~ ${formatDateDot(periodEnd)}`,
      );
      setCouponCodeInput('');
      setCouponModalOpen(false);
    } catch (err) {
      console.error(err);
      if (isActivePassExistsError(err)) {
        window.alert(activePassBlockedMessage('그룹 이용권'));
        return;
      }
      const localMatch = groupHistory.find(
        (g) => !g.refundedAt && g.couponCode.toUpperCase() === parsed,
      );
      if (localMatch) {
        applyLocalMatch(localMatch);
        return;
      }
      window.alert(
        isPassCodeRedeemExhaustedError(err)
          ? '유효하지 않거나 이미 소진·환불된 그룹 이용권 코드입니다.'
          : '그룹 이용권 코드 등록에 실패했습니다. 로그인 상태와 코드를 확인해 주세요.',
      );
    }
  }, [assertNoActivePass, couponCodeInput, groupHistory, reloadPassStateFromServer]);

  const registerEventCouponCode = useCallback(async () => {
    const parsed = parseEventCouponCodeInput(couponCodeInput);
    if (!parsed) {
      window.alert(
        `이벤트 이용권 코드 형식이 올바르지 않습니다.\n예: EVT- 로 시작하고 뒤에 ${PASS_CODE_BODY_LENGTH}자(영문·숫자, I/O/0/1 제외)입니다.`,
      );
      return;
    }
    if (!assertNoActivePass('이벤트 이용권')) return;
    try {
      const redeemed = await redeemEventPassCodeRemote(parsed);
      const periodStart = parseLocalIsoDateOnly(`${redeemed.paid_plan_start_month}-01`);
      const periodEnd = parseLocalIsoDateOnly(String(redeemed.plan_end_iso).slice(0, 10));
      if (!periodStart || !periodEnd) {
        window.alert('코드는 등록되었으나 이용 기간 정보가 올바르지 않습니다. 관리자에게 문의해 주세요.');
        return;
      }
      await reloadPassStateFromServer();
      const row = { title: redeemed.event_name || '이벤트 이용권(코드)' };
      window.alert(
        `이벤트 이용권이 적용되었습니다.\n「${row.title}」 / ${formatDateDot(periodStart)} ~ ${formatDateDot(periodEnd)}`,
      );
      setCouponCodeInput('');
      setCouponModalOpen(false);
    } catch (err) {
      console.error(err);
      window.alert(
        isActivePassExistsError(err)
          ? activePassBlockedMessage('이용권')
          : isPassCodeRedeemExhaustedError(err)
          ? '유효하지 않거나 이미 소진·마감된 이벤트 이용권 코드입니다.'
          : '이벤트 이용권 코드 등록에 실패했습니다. 로그인 상태와 코드를 확인해 주세요.',
      );
    }
  }, [assertNoActivePass, couponCodeInput, reloadPassStateFromServer]);

  const registerBetaTesterCouponCode = useCallback(async () => {
    const parsed = parseBetaTesterCouponCodeInput(couponCodeInput);
    if (!parsed) {
      window.alert(
        `베타 테스터 이용권 코드 형식이 올바르지 않습니다.\n예: CBT- 로 시작하고 뒤에 ${PASS_CODE_BODY_LENGTH}자(영문·숫자, I/O/0/1 제외)입니다.`,
      );
      return;
    }
    if (!assertNoActivePass('베타 테스터 이용권')) return;
    try {
      const redeemed = await redeemEventPassCodeRemote(parsed);
      const periodStart = parseLocalIsoDateOnly(`${redeemed.paid_plan_start_month}-01`);
      const periodEnd = parseLocalIsoDateOnly(String(redeemed.plan_end_iso).slice(0, 10));
      if (!periodStart || !periodEnd) {
        window.alert('코드는 등록되었으나 이용 기간 정보가 올바르지 않습니다. 관리자에게 문의해 주세요.');
        return;
      }
      await reloadPassStateFromServer();
      const row = { title: redeemed.event_name || '베타 테스터 이용권(코드)' };
      window.alert(
        `베타 테스터 이용권이 적용되었습니다.\n「${row.title}」 / ${formatDateDot(periodStart)} ~ ${formatDateDot(periodEnd)}`,
      );
      setCouponCodeInput('');
      setCouponModalOpen(false);
    } catch (err) {
      console.error(err);
      window.alert(
        isActivePassExistsError(err)
          ? activePassBlockedMessage('이용권')
          : isPassCodeRedeemExhaustedError(err)
          ? '유효하지 않거나 이미 소진·마감된 베타 테스터 이용권 코드입니다.'
          : '베타 테스터 이용권 코드 등록에 실패했습니다. 로그인 상태와 코드를 확인해 주세요.',
      );
    }
  }, [assertNoActivePass, couponCodeInput, reloadPassStateFromServer]);

  const submitCouponModal = useCallback(() => {
    if (requireLoginForCheckout) {
      savePendingCouponCheckout(couponModalKind, couponCodeInput);
      promptLoginForCheckout();
      return;
    }
    if (couponModalKind === 'group') void registerGroupCouponCode();
    else if (couponModalKind === 'beta') void registerBetaTesterCouponCode();
    else void registerEventCouponCode();
  }, [
    couponCodeInput,
    couponModalKind,
    promptLoginForCheckout,
    registerBetaTesterCouponCode,
    registerEventCouponCode,
    registerGroupCouponCode,
    requireLoginForCheckout,
  ]);

  /** 랜딩에서 로그인 후 /subscription 진입 시 보류된 결제·쿠폰 이어서 진행 */
  const pendingResumeActionRef = useRef<'confirmIndividual' | 'confirmGroup' | 'submitCoupon' | null>(
    null,
  );

  useEffect(() => {
    if (requireLoginForCheckout || resumeCheckoutRef.current) return;
    resumeCheckoutRef.current = true;
    const pending = takePendingPassCheckout();
    if (!pending) return;
    if (pending.action === 'confirmIndividual') {
      pendingResumeActionRef.current = 'confirmIndividual';
      setIndividualAgreeRefund(true);
      setModal({ kind: 'individual', pricing: revivePricing(pending.pricing) });
      return;
    }
    if (pending.action === 'confirmGroup') {
      pendingResumeActionRef.current = 'confirmGroup';
      setGroupForm(pending.groupForm);
      setGroupBillingStartDraft(formatGroupBillingStartDraft(pending.groupForm.billingStartIso));
      setModal({ kind: 'group', basePricing: revivePricing(pending.basePricing) });
      return;
    }
    pendingResumeActionRef.current = 'submitCoupon';
    setCouponModalKind(pending.couponModalKind);
    setCouponCodeInput(pending.couponCodeInput);
    setCouponModalOpen(true);
  }, [requireLoginForCheckout]);

  useEffect(() => {
    const action = pendingResumeActionRef.current;
    if (!action) return;
    if (action === 'confirmIndividual') {
      if (modal?.kind !== 'individual') return;
      pendingResumeActionRef.current = null;
      queueMicrotask(() => {
        confirmIndividualPurchase();
      });
      return;
    }
    if (action === 'confirmGroup') {
      if (modal?.kind !== 'group') return;
      pendingResumeActionRef.current = null;
      queueMicrotask(() => {
        void confirmGroupPurchase();
      });
      return;
    }
    if (!couponModalOpen) return;
    pendingResumeActionRef.current = null;
    queueMicrotask(() => {
      submitCouponModal();
    });
  }, [confirmGroupPurchase, confirmIndividualPurchase, couponModalOpen, modal, submitCouponModal]);

  const pageBody = (
    <>
    <div className={isLandingPurchase ? 'cp-sub-pass-main-host--flat' : 'cp-sub-pass-main'}>
    <section
      className={`cp-sub-pass-page${isLandingPurchase ? ' cp-sub-pass-page--landing-purchase' : ''}`}
      aria-label={isLandingPurchase ? '이용권 안내' : '이용권 구매 및 관리'}
    >
      {isLandingPurchase ? null : (
      <header className="cp-sub-pass-header">
        <h1 className="cp-sub-pass-title">이용권 구매 및 관리</h1>
      </header>
      )}

      {!isLandingPurchase ? (
      <>
      <div className="cp-sub-pass-active-strip" aria-label="현재 플랜">
        <div className="cp-sub-pass-active-strip-inner">
          {!passHydrated ? (
            <p className="cp-sub-pass-hydrate-loading" role="status" aria-live="polite">
              이용권 정보를 불러오는 중…
            </p>
          ) : hasStripPlanTiles ? (
            <>
              <div className="cp-sub-pass-active-passes" role="list">
                {metaPlanPro && stripPlanTiles.length === 0 ? (
                  <div className="cp-sub-pass-active-pass-block" role="listitem">
                    <p className="cp-sub-pass-active-label">이용기간</p>
                    <p className="cp-sub-pass-active-dates cp-sub-pass-active-dates--hero">프로 플랜</p>
                    <p className="cp-sub-pass-active-pass-title">테스트 계정</p>
                    <p className="cp-sub-pass-active-pass-kind">프로 플랜</p>
                  </div>
                ) : null}
                {stripPlanTiles.map((tile) => (
                  <div
                    key={tile.key}
                    className={`cp-sub-pass-active-pass-block${tile.phase === 'upcoming' ? ' cp-sub-pass-active-pass-block--upcoming' : ''}`}
                    role="listitem"
                  >
                    <p className="cp-sub-pass-active-label">이용기간</p>
                    <p className="cp-sub-pass-active-dates cp-sub-pass-active-dates--hero">{tile.periodLine}</p>
                    <p className="cp-sub-pass-active-pass-title">{tile.schoolYearTitle}</p>
                    <p className="cp-sub-pass-active-pass-kind">{tile.kindLabel}</p>
                    {tile.groupPurchaserAccountLabel != null && tile.groupCouponCode != null ? (
                      <div className="cp-sub-pass-active-tile-group-meta">
                        <p className="cp-sub-pass-active-tile-group-line">
                          <span className="cp-sub-pass-active-tile-group-k">그룹 이용권 구매자 (계정)</span>{' '}
                          <span className="cp-sub-pass-active-tile-group-v">{tile.groupPurchaserAccountLabel}</span>
                        </p>
                        <p className="cp-sub-pass-active-tile-group-line">
                          <span className="cp-sub-pass-active-tile-group-k">쿠폰 코드</span>{' '}
                          <span className="cp-sub-pass-active-tile-group-v">{tile.groupCouponCode}</span>
                        </p>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              {!calendarPaidActive ? (
                <p className="cp-sub-pass-active-free-line">
                  오늘 날짜 기준 실제 과금·앱 혜택은 프리 이용권이며, 위 시작일부터 유료 구간이 적용됩니다.
                </p>
              ) : null}
            </>
          ) : (
            <>
              <div className="cp-sub-pass-active-free-card">
                <p className="cp-sub-pass-active-label">현재 플랜</p>
                <p className="cp-sub-pass-active-dates cp-sub-pass-active-dates--hero">프리 이용권</p>
              </div>
              <p className="cp-sub-pass-active-free-line">유료 이용권이 적용되어 있지 않습니다.</p>
            </>
          )}
          {passHydrated ? (
            <button
              type="button"
              className={
                'cp-sub-pass-payment-history-toggle' +
                (passPaymentHistoryOpen ? ' is-open' : '')
              }
              aria-expanded={passPaymentHistoryOpen}
              aria-controls="cp-sub-pass-payment-history-panel"
              onClick={() => setPassPaymentHistoryOpen((v) => !v)}
            >
              {passPaymentHistoryOpen ? '결제 내역 닫기' : '결제 내역 확인'}
            </button>
          ) : null}
        </div>

        {passHydrated && passPaymentHistoryOpen ? (
          <div
            id="cp-sub-pass-payment-history-panel"
            className="cp-sub-pass-payment-history-panel"
            role="region"
            aria-label="결제 내역"
          >
      {/* 개인 이용권 구매 이력 */}
      <section
        className="cp-sub-pass-section-block cp-sub-pass-history"
        aria-labelledby="cp-sub-pass-ind-history-title"
      >
        <div className="cp-sub-pass-history-head">
          <h2 id="cp-sub-pass-ind-history-title" className="cp-sub-pass-history-title">
            개별 이용권 구매 이력
          </h2>
          {individualHistory.length > 0 || eventHistory.length > 0 ? (
            <button
              type="button"
              className={`cp-sub-pass-history-link cp-sub-pass-history-link--toggle${individualHistoryDetailOpen ? ' is-open' : ''}`}
              aria-expanded={individualHistoryDetailOpen}
              onClick={() => setIndividualHistoryDetailOpen((v) => !v)}
            >
              {individualHistoryDetailOpen ? '상세 결제 내역 닫기' : '상세 결제 내역 확인'}
            </button>
          ) : null}
        </div>
        {individualHistory.length === 0 && eventHistory.length === 0 ? (
          <div className="cp-sub-pass-history-collapsed-hint">현재 이용중인 이용권이 없습니다.</div>
        ) : (
          <>
            {!individualHistoryDetailOpen &&
            individualHistoryRowsVisible.length === 0 &&
            eventHistory.filter(
              (e) =>
                !e.refundedAt &&
                (isInclusiveDateRangeActive(e.periodStart, e.periodEnd) ||
                  isUpcomingPaidPassWindow(e.periodStart, e.periodEnd))
            ).length === 0 ? (
              <div className="cp-sub-pass-history-collapsed-hint">
                활성화된 개별·이벤트 이용권 구매 이력이 없습니다.
              </div>
            ) : null}
            <ul className="cp-sub-pass-ind-list">
              {individualHistoryRowsVisible.map((row) => {
                const peers = individualHistory.filter((x) => !x.refundedAt);
                const indPreview = computeIndividualPassRefundPreview(row, new Date(), peers);
                const refundStatusText = row.refundedAt
                  ? '환불 완료'
                  : indPreview.eligible
                    ? '환불 가능'
                    : '환불 불가';
                const refundBtnLabel = row.refundedAt
                  ? '환불 완료'
                  : indPreview.eligible
                    ? '환불'
                    : '환불 요청 불가';
                const isCardActive =
                  !row.refundedAt && isInclusiveDateRangeActive(row.periodStart, row.periodEnd);
                const isCardUpcoming =
                  !row.refundedAt && isUpcomingPaidPassWindow(row.periodStart, row.periodEnd);
                const isRefunded = Boolean(row.refundedAt);
                return (
                  <li
                    key={row.id}
                    className={`cp-sub-pass-ind-card${isRefunded ? ' cp-sub-pass-ind-card--refunded' : ''}${isCardActive ? ' cp-sub-pass-ind-card--active' : ''}${isCardUpcoming ? ' cp-sub-pass-ind-card--upcoming' : ''}`}
                  >
                    <div className="cp-sub-pass-rec-head">
                      <span className="cp-sub-pass-purchase-badge" aria-hidden>
                        구매일 {formatDateDot(row.purchasedAt)}
                      </span>
                      <div className="cp-sub-pass-group-tags">
                        <span className="cp-sub-pass-mini-tag">개별 이용권</span>
                        <span className="cp-sub-pass-mini-tag">일반 이용권</span>
                      </div>
                    </div>
                    <div className="cp-sub-pass-ind-card-grid">
                      <div className="cp-sub-pass-ind-col cp-sub-pass-ind-col--purchase">
                        <p className="cp-sub-pass-ind-sec-title">이용 기간</p>
                        <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                          {formatDateDot(row.periodStart)} ~ {formatDateDot(row.periodEnd)}
                        </p>
                        <p className="cp-sub-pass-ind-pay-line">결제금액 : {formatKrw(row.amountWon)}원</p>
                      </div>
                      <div className="cp-sub-pass-ind-col cp-sub-pass-ind-col--refund">
                        {isRefunded ? (
                          <>
                            <p className="cp-sub-pass-ind-sec-title">환불 완료</p>
                            <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                              {row.refundedAt ? formatDateDot(row.refundedAt) : '—'}
                            </p>
                            <p className="cp-sub-pass-ind-refund-status cp-sub-pass-ind-refund-status--done">
                              환불 처리가 완료되었습니다.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="cp-sub-pass-ind-sec-title">환불 정보</p>
                            <p className="cp-sub-pass-group-label">환불 안내</p>
                            <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight cp-sub-pass-ind-refund-policy">
                              언제든 신청 가능 · 이용일은 1일 {formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원 공제
                            </p>
                            <p className="cp-sub-pass-ind-refund-status">{refundStatusText}</p>
                            <button
                              type="button"
                              className={`cp-sub-pass-refund-btn${indPreview.eligible && !row.refundedAt ? ' cp-sub-pass-refund-btn--active' : ''}`}
                              disabled={Boolean(row.refundedAt) || !indPreview.eligible}
                              title={!indPreview.eligible && !row.refundedAt ? indPreview.reason : undefined}
                              onClick={() => openRefundModal('individual', row.id)}
                            >
                              {refundBtnLabel}
                            </button>
                          </>
                        )}
                      </div>
                      <div className="cp-sub-pass-ind-col cp-sub-pass-ind-col--docs">
                        <p className="cp-sub-pass-ind-sec-title">증빙서류</p>
                        <button
                          type="button"
                          className="cp-btn-secondary cp-sub-pass-receipt-btn"
                          onClick={() =>
                            setIndividualReceipt(
                              toPassReceiptView(row, '클래스 플래너 개별 이용권'),
                            )
                          }
                        >
                          구매 영수증
                        </button>
                        {isRefunded ? (
                          <p className="cp-sub-pass-ind-receipt-disclaimer">
                            카드 결제가 없는 건(쿠폰·관리자 발급 등)은 증빙을 제공하지 않습니다.
                          </p>
                        ) : (
                          <p className="cp-sub-pass-group-receipt-note">
                            결제·PG 연동 시 실제 매출전표와 동일하게 확장됩니다.
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
              {(individualHistoryDetailOpen
                ? eventHistory
                : eventHistory.filter(
                    (e) =>
                      !e.refundedAt &&
                      (isInclusiveDateRangeActive(e.periodStart, e.periodEnd) ||
                        isUpcomingPaidPassWindow(e.periodStart, e.periodEnd))
                  )
              ).map((row) => {
                const evtPreview = computeEventPassRefundPreview(row);
                const refundDisabled = row.refundDisabled || row.kind === 'free';
                const refundStatusText = row.refundedAt
                  ? '환불 완료'
                  : refundDisabled
                    ? '환불 불가'
                    : evtPreview.eligible
                      ? '환불 가능'
                      : '환불 불가';
                const refundBtnLabel = row.refundedAt
                  ? '환불 완료'
                  : refundDisabled
                    ? '환불 불가'
                    : evtPreview.eligible
                      ? '환불'
                      : '환불 요청 불가';
                const isCardActive =
                  !row.refundedAt && isInclusiveDateRangeActive(row.periodStart, row.periodEnd);
                const isCardUpcoming =
                  !row.refundedAt && isUpcomingPaidPassWindow(row.periodStart, row.periodEnd);
                const isRefunded = Boolean(row.refundedAt);
                return (
                  <li
                    key={row.id}
                    className={`cp-sub-pass-ind-card${isRefunded ? ' cp-sub-pass-ind-card--refunded' : ''}${isCardActive ? ' cp-sub-pass-ind-card--active' : ''}${isCardUpcoming ? ' cp-sub-pass-ind-card--upcoming' : ''}`}
                  >
                    <div className="cp-sub-pass-rec-head">
                      <span className="cp-sub-pass-purchase-badge" aria-hidden>
                        구매일 {formatDateDot(row.purchasedAt)}
                      </span>
                      <div className="cp-sub-pass-group-tags">
                        <span className="cp-sub-pass-mini-tag">개별 이용권</span>
                        <span className="cp-sub-pass-mini-tag">{eventPassKindLabel(row)}</span>
                      </div>
                    </div>
                    <div className="cp-sub-pass-ind-card-grid">
                      <div className="cp-sub-pass-ind-col cp-sub-pass-ind-col--purchase">
                        <p className="cp-sub-pass-ind-sec-title">이용 기간</p>
                        <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                          {formatDateDot(row.periodStart)} ~ {formatDateDot(row.periodEnd)}
                        </p>
                        <p className="cp-sub-pass-ind-pay-line">
                          결제금액 : {formatKrw(row.amountWon)}원
                        </p>
                        <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                          {row.title}
                        </p>
                      </div>
                      <div className="cp-sub-pass-ind-col cp-sub-pass-ind-col--refund">
                        {isRefunded ? (
                          <>
                            <p className="cp-sub-pass-ind-sec-title">환불 완료</p>
                            <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                              {row.refundedAt ? formatDateDot(row.refundedAt) : '—'}
                            </p>
                            <p className="cp-sub-pass-ind-refund-status cp-sub-pass-ind-refund-status--done">
                              환불 처리가 완료되었습니다.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="cp-sub-pass-ind-sec-title">환불 정보</p>
                            <p className="cp-sub-pass-group-label">환불 안내</p>
                            <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight cp-sub-pass-ind-refund-policy">
                              {refundDisabled
                                ? '해당 이용권은 환불 절차가 없습니다.'
                                : `사용일 × 1일 ${formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원 공제 · 7일 경과 시 잔액 10% 수수료`}
                            </p>
                            <p className="cp-sub-pass-ind-refund-status">{refundStatusText}</p>
                            <button
                              type="button"
                              className={`cp-sub-pass-refund-btn${
                                !refundDisabled && evtPreview.eligible && !row.refundedAt
                                  ? ' cp-sub-pass-refund-btn--active'
                                  : ''
                              }`}
                              disabled={
                                Boolean(row.refundedAt) || refundDisabled || !evtPreview.eligible
                              }
                              title={
                                refundDisabled
                                  ? '무료 이벤트 이용권은 환불할 수 없습니다.'
                                  : !evtPreview.eligible && !row.refundedAt
                                    ? evtPreview.reason
                                    : undefined
                              }
                              onClick={() => openRefundModal('event', row.id)}
                            >
                              {refundBtnLabel}
                            </button>
                          </>
                        )}
                      </div>
                      <div className="cp-sub-pass-ind-col cp-sub-pass-ind-col--docs">
                        <p className="cp-sub-pass-ind-sec-title">증빙서류</p>
                        {row.kind === 'paid' ? (
                          <>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-sub-pass-receipt-btn"
                              onClick={() =>
                                setIndividualReceipt(
                                  toPassReceiptView(
                                    row,
                                    `클래스 플래너 유료 이벤트 이용권 (${row.title})`,
                                  ),
                                )
                              }
                            >
                              구매 영수증
                            </button>
                            {isRefunded ? (
                              <p className="cp-sub-pass-ind-receipt-disclaimer">
                                카드 결제가 없는 건(쿠폰·관리자 발급 등)은 증빙을 제공하지 않습니다.
                              </p>
                            ) : (
                              <p className="cp-sub-pass-group-receipt-note">
                                결제·PG 연동 시 실제 매출전표와 동일하게 확장됩니다.
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="cp-sub-pass-group-receipt-note">
                            무료 이벤트 이용권은 구매 영수증을 제공하지 않습니다.
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        <div className="cp-sub-pass-history-rule" role="separator" />
        <div className="cp-sub-pass-history-summary">
          {individualHistoryDetailOpen ? (
            <>
              <p className="cp-sub-pass-history-total cp-sub-pass-history-total--accent">
                총 서비스 이용 결제액 :{' '}
                {formatKrw(
                  individualGrossPaid + eventHistory.reduce((s, e) => s + e.amountWon, 0)
                )}
                원
              </p>
              {individualRefundedSum > 0 ? (
                <>
                  <p className="cp-sub-pass-history-total cp-sub-pass-history-total--refund">
                    환불 받은 금액 합계 : {formatKrw(individualRefundedSum)}원
                  </p>
                  <p className="cp-sub-pass-history-total cp-sub-pass-history-total--net">
                    순 결제액 : {formatKrw(individualGrossPaid - individualRefundedSum)}원
                  </p>
                </>
              ) : null}
            </>
          ) : (
            <>
              <p className="cp-sub-pass-history-total cp-sub-pass-history-total--accent">
                구입한 이용권 수 : {individualPurchasedCount}개
              </p>
              <p className="cp-sub-pass-history-total cp-sub-pass-history-total--count">
                환불한 이용권 수 : {individualRefundedCount}개
              </p>
            </>
          )}
        </div>
      </section>

      {individualReceipt ? (
        <div
          className="cp-modal-backdrop cp-pass-receipt-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-edu-receipt-title"
          onClick={() => setIndividualReceipt(null)}
        >
          <div
            className="cp-modal cp-edu-receipt-modal cp-pass-receipt-modal-root"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-edu-receipt-head">
              <h2 id="cp-edu-receipt-title" className="cp-edu-receipt-title">
                구매 영수증
              </h2>
              <button
                type="button"
                className="cp-edu-receipt-close"
                aria-label="닫기"
                onClick={() => setIndividualReceipt(null)}
              >
                ×
              </button>
            </div>
            <div className="cp-edu-receipt-body">
              {(() => {
                const r = individualReceipt;
                const { taxable, vat, exempt } = inclusiveTotalToTaxableAndVat(r.amountWon);
                const orderId = individualPassOrderLicId(r);
                return (
                  <>
                    <dl className="cp-edu-receipt-dl">
                      <div className="cp-edu-receipt-row">
                        <dt>주문번호</dt>
                        <dd>{orderId}</dd>
                      </div>
                      <div className="cp-edu-receipt-row">
                        <dt>구매자명</dt>
                        <dd>
                          <strong>{receiptBuyerName}</strong>
                        </dd>
                      </div>
                      <div className="cp-edu-receipt-row">
                        <dt>계정 이메일</dt>
                        <dd className="cp-edu-receipt-dd-muted">{receiptBuyerEmail}</dd>
                      </div>
                      <div className="cp-edu-receipt-row">
                        <dt>구매상품</dt>
                        <dd>
                          <strong>{r.productLabel}</strong>
                        </dd>
                      </div>
                      <div className="cp-edu-receipt-row">
                        <dt>결제일시</dt>
                        <dd>{formatPaymentLogDateTime(r.purchasedAt)}</dd>
                      </div>
                      <div className="cp-edu-receipt-row">
                        <dt>결제방법</dt>
                        <dd>카드 결제 (데모) · Mastercard (체크)</dd>
                      </div>
                    </dl>
                    <div className="cp-edu-receipt-rule" />
                    <dl className="cp-edu-receipt-dl">
                      <div className="cp-edu-receipt-row">
                        <dt>과세 금액</dt>
                        <dd>{formatKrw(taxable)}</dd>
                      </div>
                      <div className="cp-edu-receipt-row">
                        <dt>비과세 금액</dt>
                        <dd>{formatKrw(exempt)}</dd>
                      </div>
                      <div className="cp-edu-receipt-row">
                        <dt>부가세 금액</dt>
                        <dd>{formatKrw(vat)}</dd>
                      </div>
                    </dl>
                    <div className="cp-edu-receipt-rule" />
                    <div className="cp-edu-receipt-total-row">
                      <span className="cp-edu-receipt-total-label">결제 금액 (KRW)</span>
                      <span className="cp-edu-receipt-total-val">{formatKrw(r.amountWon)}원</span>
                    </div>
                    {r.refundedAt && r.refundAmountWon != null ? (
                      <>
                        <div className="cp-edu-receipt-rule" />
                        <div className="cp-edu-receipt-refund-block">
                          <p className="cp-edu-receipt-refund-title">환불 정보</p>
                          <p className="cp-edu-receipt-refund-note">
                            KG이니시스·포트원에 등록된 실제 카드 취소(환불) 내역입니다.
                          </p>
                          <dl className="cp-edu-receipt-dl cp-edu-receipt-dl--compact">
                            <div className="cp-edu-receipt-row">
                              <dt>환불일시</dt>
                              <dd>{formatPaymentLogDateTime(r.refundedAt)}</dd>
                            </div>
                            <div className="cp-edu-receipt-row">
                              <dt>결제 금액</dt>
                              <dd>{formatKrw(r.amountWon)}원</dd>
                            </div>
                          </dl>
                          <p className="cp-edu-receipt-refund-pg-note">포트원(PG) 등록 취소 내역 기준</p>
                          <div className="cp-edu-receipt-refund-rule" />
                          <div className="cp-edu-receipt-refund-total">
                            <span>환불 금액</span>
                            <span>{formatKrw(r.refundAmountWon)}원</span>
                          </div>
                        </div>
                      </>
                    ) : null}
                    <div className="cp-edu-receipt-seller-box">
                      <p className="cp-edu-receipt-seller-title">
                        판매처 ({PLATFORM_COMPANY.brandName})
                      </p>
                      <p className="cp-edu-receipt-seller-lines">
                        {PLATFORM_COMPANY.operatorLine}
                        <br />
                        {PLATFORM_COMPANY.businessNumber}
                        <br />
                        {PLATFORM_COMPANY.mailOrderLicense}
                        <br />
                        {PLATFORM_COMPANY.address}
                        <br />
                        문의: {PLATFORM_COMPANY.supportEmail}
                      </p>
                    </div>
                    <div className="cp-edu-receipt-pg-foot">
                      본 결제는 KG이니시스를 통해 처리되었습니다. 공식 매출전표는 KG이니시스 영수증 확인 창을 통해 확인할 수
                      있습니다.
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="cp-edu-receipt-actions">
              <button
                type="button"
                className="cp-edu-receipt-btn cp-edu-receipt-btn--primary-full"
                onClick={() =>
                  window.alert('KG이니시스 매출전표 확인 창은 PG 연동 후 제공됩니다. (데모)')
                }
              >
                매출전표 확인 창 바로가기
              </button>
              <div className="cp-edu-receipt-btn-row">
                <button type="button" className="cp-edu-receipt-btn cp-edu-receipt-btn--outline" onClick={() => window.print()}>
                  인쇄
                </button>
                <button
                  type="button"
                  className="cp-edu-receipt-btn cp-edu-receipt-btn--primary"
                  onClick={() => window.alert('PDF 저장은 추후 제공됩니다. (데모)')}
                >
                  PDF 저장
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* 그룹 이용권 구매 이력 */}
      <section
        className="cp-sub-pass-section-block cp-sub-pass-history"
        aria-labelledby="cp-sub-pass-grp-history-title"
      >
        <div className="cp-sub-pass-history-head">
          <h2 id="cp-sub-pass-grp-history-title" className="cp-sub-pass-history-title">
            그룹 이용권 구매 이력
          </h2>
          <button
            type="button"
            className={`cp-sub-pass-history-link cp-sub-pass-history-link--toggle${groupHistoryDetailOpen ? ' is-open' : ''}`}
            aria-expanded={groupHistoryDetailOpen}
            onClick={() => {
              if (groupHistory.length === 0) {
                setGroupHistoryDetailOpen(false);
                showInfo('등록된 그룹 이용권 구매 내역이 없습니다.');
                return;
              }
              setGroupHistoryDetailOpen((v) => !v);
            }}
          >
            {groupHistoryDetailOpen ? '상세 결제 내역 닫기' : '상세 결제 내역 확인'}
          </button>
        </div>
        {groupHistory.length === 0 ? (
          <div className="cp-sub-pass-history-collapsed-hint">
            등록된 그룹 이용권 구매 내역이 없습니다.
          </div>
        ) : (
          <>
            {!groupHistoryDetailOpen && groupHistoryRowsVisible.length === 0 ? (
              <div className="cp-sub-pass-history-collapsed-hint">
                이 세션에서 쿠폰을 등록해 표시 중인 그룹 이용권이 없습니다. 구매만 한 건은{' '}
                <strong>「쿠폰 코드 등록」</strong> 후 이 계정에 반영됩니다.{' '}
                <strong>「상세 결제 내역 확인」</strong>을 누르면 만료·환불 건을 포함한 전체 내역을 볼 수 있습니다.
              </div>
            ) : null}
            <ul className="cp-sub-pass-group-list">
              {groupHistoryRowsVisible.map((g) => {
              const grpPreview = computeGroupPassRefundPreview(g);
              const refundStatusText = g.refundedAt
                ? '환불 완료'
                : grpPreview.eligible
                  ? '환불 가능'
                  : '환불 불가';
              const refundBtnLabel = g.refundedAt
                ? '환불 완료'
                : grpPreview.eligible
                  ? '환불'
                  : '환불 요청 불가';
              const registeredHere = isGroupPassRegisteredOnThisSession(g, sessionGroupPassBinding);
              const isCardActive =
                !g.refundedAt &&
                registeredHere &&
                isInclusiveDateRangeActive(g.activationPeriodStart, g.activationPeriodEnd);
              const isCardUpcoming =
                !g.refundedAt &&
                registeredHere &&
                isUpcomingPaidPassWindow(g.activationPeriodStart, g.activationPeriodEnd);
              const isAwaitingCoupon =
                !g.refundedAt &&
                !registeredHere &&
                Date.now() <= endOfDayOnly(g.activationPeriodEnd).getTime();
              return (
                <li
                  key={g.id}
                  className={`cp-sub-pass-grp-card${g.refundedAt ? ' cp-sub-pass-grp-card--refunded' : ''}${isCardActive ? ' cp-sub-pass-grp-card--active' : ''}${isCardUpcoming ? ' cp-sub-pass-grp-card--upcoming' : ''}${isAwaitingCoupon ? ' cp-sub-pass-grp-card--awaiting-coupon' : ''}`}
                >
                  <div className="cp-sub-pass-rec-head">
                    <span className="cp-sub-pass-purchase-badge" aria-hidden>
                      구매일 {formatDateDot(g.purchasedAt)}
                    </span>
                    <div className="cp-sub-pass-group-tags">
                      <span className="cp-sub-pass-mini-tag">그룹 이용권</span>
                      {isAwaitingCoupon ? (
                        <span className="cp-sub-pass-mini-tag cp-sub-pass-mini-tag--awaiting-coupon">
                          이 계정 미등록
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="cp-sub-pass-grp-card-surface">
                    <div className="cp-sub-pass-grp-card-grid">
                      <div className="cp-sub-pass-grp-col cp-sub-pass-grp-col--purchase">
                        <p className="cp-sub-pass-ind-sec-title">구매 내역</p>
                        <p className="cp-sub-pass-group-label">쿠폰 적용 시 이용 기간</p>
                        <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                          {formatDateDot(g.activationPeriodStart)} ~ {formatDateDot(g.activationPeriodEnd)}
                        </p>
                        {isAwaitingCoupon ? (
                          <p className="cp-sub-pass-group-awaiting-note" role="note">
                            이 브라우저에서는 아직 코드를 등록하지 않았습니다. 상단 <strong>쿠폰 코드 등록</strong>에
                            구매 시 발급된 코드를 입력하면 이 계정에 반영됩니다.
                          </p>
                        ) : null}
                        <p className="cp-sub-pass-group-meta">구매 이용권 수 : {g.quantity}개</p>
                        <p
                          className={`cp-sub-pass-group-active-pill${
                            !g.refundedAt && g.activatedCount < g.quantity
                              ? ' cp-sub-pass-group-active-pill--pending'
                              : ''
                          }`}
                          aria-label={`활성 등록 ${g.activatedCount}명, 구매 이용권 ${g.quantity}명`}
                        >
                          활성: {g.activatedCount} / {g.quantity}
                        </p>
                        <p className="cp-sub-pass-ind-pay-line">결제금액 : {formatKrw(g.totalWon)}원</p>
                        <p className="cp-sub-pass-group-label">그룹 이용권 구매자 (계정)</p>
                        <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                          {groupPassPurchaserAccountLabel(g)}
                        </p>
                        <div className="cp-sub-pass-group-code cp-sub-pass-grp-purchase-code-row">
                          <span className="cp-sub-pass-grp-purchase-code-text">
                            쿠폰 코드 : <strong>{g.couponCode}</strong>
                          </span>
                          {String(g.couponCode ?? '').trim() ? (
                            <button
                              type="button"
                              className="cp-btn-secondary cp-sub-pass-grp-purchase-code-copy-btn"
                              aria-label="쿠폰 코드 복사"
                              onClick={() => void copyGroupHistoryCouponCode(g.id, g.couponCode ?? '')}
                            >
                              {groupHistoryCouponCopiedId === g.id ? '복사됨' : '복사'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="cp-sub-pass-grp-col cp-sub-pass-grp-col--refund">
                        <p className="cp-sub-pass-ind-sec-title">환불 정보</p>
                        <p className="cp-sub-pass-group-label">그룹 이용권 구매자 (계정)</p>
                        <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                          {groupPassPurchaserAccountLabel(g)}
                        </p>
                        <p className="cp-sub-pass-group-label">쿠폰 받기 이메일</p>
                        <p className="cp-sub-pass-group-email">
                          <span className="cp-sub-pass-grp-email-check" aria-hidden>
                            ✓
                          </span>{' '}
                          {g.couponEmail}
                        </p>
                        <p className="cp-sub-pass-group-code">
                          쿠폰 코드 : <strong>{g.couponCode}</strong>
                        </p>
                        <p className="cp-sub-pass-group-label">환불 가능 기간</p>
                        <p className="cp-sub-pass-group-value cp-sub-pass-ind-value-tight">
                          {formatDateDot(g.refundPeriodStart)} ~ {formatDateDot(g.refundPeriodEnd)}
                        </p>
                        {g.refundedAt && g.refundAmountWon != null ? (
                          <p className="cp-sub-pass-ind-refund-summary">
                            환불 {formatKrw(g.refundAmountWon)}원 · {formatDateDot(g.refundedAt)}
                          </p>
                        ) : (
                          <p className="cp-sub-pass-ind-refund-status">{refundStatusText}</p>
                        )}
                        <button
                          type="button"
                          className={`cp-sub-pass-refund-btn${grpPreview.eligible && !g.refundedAt ? ' cp-sub-pass-refund-btn--active' : ''}`}
                          disabled={Boolean(g.refundedAt) || !grpPreview.eligible}
                          title={!grpPreview.eligible && !g.refundedAt ? grpPreview.reason : undefined}
                          onClick={() => openRefundModal('group', g.id)}
                        >
                          {refundBtnLabel}
                        </button>
                      </div>
                      <div className="cp-sub-pass-grp-col cp-sub-pass-grp-col--docs">
                        <p className="cp-sub-pass-ind-sec-title">증빙서류</p>
                        <button type="button" className="cp-btn-secondary cp-sub-pass-receipt-btn" disabled>
                          구매 영수증
                        </button>
                        <p className="cp-sub-pass-group-receipt-note">
                          카드 결제 등 연동 후 영수증이 제공됩니다. (데모 화면)
                        </p>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
            </ul>
          </>
        )}
        <div className="cp-sub-pass-history-rule" role="separator" />
        <div className="cp-sub-pass-history-summary">
          {groupHistoryDetailOpen ? (
            <>
              <p className="cp-sub-pass-history-total cp-sub-pass-history-total--accent">
                총 그룹 이용권 결제액 : {formatKrw(groupGrossPaid)}원
              </p>
              {groupRefundedSum > 0 ? (
                <>
                  <p className="cp-sub-pass-history-total cp-sub-pass-history-total--refund">
                    환불 받은 금액 합계 : {formatKrw(groupRefundedSum)}원
                  </p>
                  <p className="cp-sub-pass-history-total cp-sub-pass-history-total--net">
                    순 결제액 : {formatKrw(groupGrossPaid - groupRefundedSum)}원
                  </p>
                </>
              ) : null}
            </>
          ) : (
            <>
              <p className="cp-sub-pass-history-total cp-sub-pass-history-total--accent">
                구입한 이용권 수 : {groupPurchasedCount}개
              </p>
              <p className="cp-sub-pass-history-total cp-sub-pass-history-total--count">
                환불한 이용권 수 : {groupRefundedCount}개
              </p>
            </>
          )}
        </div>
      </section>
          </div>
        ) : null}
      </div>
      </>
      ) : null}

      <section
        className="cp-sub-pass-section-block cp-sub-pass-purchase-block"
        aria-labelledby="cp-sub-pass-purchase-block-title"
      >
        <h2 id="cp-sub-pass-purchase-block-title" className="cp-sub-pass-purchase-block-title">
          {isLandingPurchase ? '이용권 안내' : '이용권 구입'}
        </h2>
        {!isLandingPurchase && !passHydrated ? (
          <p className="cp-sub-pass-hydrate-loading cp-sub-pass-hydrate-loading--block" role="status" aria-live="polite">
            이용권 구입 정보를 불러오는 중…
          </p>
        ) : (
        <>
        {!purchaseUiLocked && paymentMode === 'production' ? (
          <div className="cp-sub-pass-purchase-locked-banner" role="status">
            <strong>실 PG 연동 예정</strong>
            <span>
              실제 결제 모드입니다. 카드 결제(포트원 등) 연동 전까지 데모 결제를 진행할 수 없습니다.
            </span>
          </div>
        ) : null}
        <div className="cp-sub-pass-purchase-actions">
          <div className="cp-sub-pass-coupon-panel">
            <div className="cp-sub-pass-purchase-notice" role="note">
              <p>
                그룹·이벤트 쿠폰 코드는 <strong>언제든지 등록</strong>할 수 있습니다. 개별 이용권과 기간이 겹치더라도
                등록할 수 있으며, 필요하면 개별 이용권은 환불로 정리할 수 있습니다.
              </p>
              <p>쿠폰 등록 또는 결제 완료 시점부터 이용 기간이 시작됩니다.</p>
            </div>
            <button
              type="button"
              className="cp-sub-pass-coupon-register-btn"
              onClick={() => {
                setCouponModalKind('group');
                setCouponCodeInput('');
                setCouponModalOpen(true);
              }}
            >
              쿠폰 코드 등록
            </button>
          </div>
          <div className="cp-sub-pass-plan-simple-wrap">
            <div className="cp-sub-pass-plan-simple cp-sub-pass-plan-simple--cards">
              <div className="cp-sub-pass-plan-simple-cols" role="list">
                {isLandingPurchase ? (
                  <button
                    type="button"
                    role="listitem"
                    className="cp-sub-pass-plan-simple-col cp-sub-pass-plan-simple-col--buy"
                    title="회원가입 후 프리 이용권이 적용됩니다."
                    aria-label="프리 이용권 — 회원가입시 적용"
                    onClick={() => promptLoginForCheckout()}
                  >
                    <span className="cp-pass-ticket-tier cp-pass-ticket-tier--free" aria-hidden>
                      FREE 이용권
                    </span>
                    <span className="cp-sub-pass-pill cp-sub-pass-pill--muted">무료</span>
                    <h3 className="cp-sub-pass-plan-simple-title">프리 이용권</h3>
                    <div className="cp-sub-pass-plan-period-block">
                      <div className="cp-sub-pass-plan-period-label">이용권 활성 기간</div>
                      <div className="cp-sub-pass-plan-period-value">제한 없음</div>
                    </div>
                    <div className="cp-sub-pass-plan-price-stack">
                      <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                      <p className="cp-sub-pass-plan-hero-price">FREE</p>
                      <span className="cp-sub-pass-plan-action-badge">회원가입시 적용</span>
                    </div>
                    <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                    <div
                      ref={planMidFreeRef}
                      className="cp-sub-pass-plan-mid-between-dashes"
                      style={planMidSlotPx > 0 ? { minHeight: planMidSlotPx } : undefined}
                    >
                      <div className="cp-sub-pass-plan-desc-stack cp-sub-pass-plan-desc-stack--mid">
                        <p className="cp-sub-pass-plan-simple-desc cp-sub-pass-plan-simple-desc--headline">
                          AI 기능 제외 모든 기능 이용가능!
                        </p>
                      </div>
                    </div>
                    <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                    <div className="cp-sub-pass-plan-desc-stack" />
                  </button>
                ) : (
                  <div
                    className={`cp-sub-pass-plan-simple-col${planCardInUse.freeInUse ? ' cp-sub-pass-plan-simple-col--current' : ''}${planCardInUse.suppressOtherPasses ? ' cp-sub-pass-plan-simple-col--locked cp-sub-pass-plan-simple-col--next-year' : ''}`}
                    role="listitem"
                  >
                    <span className="cp-pass-ticket-tier cp-pass-ticket-tier--free" aria-hidden>
                      FREE 이용권
                    </span>
                    <span className="cp-sub-pass-pill cp-sub-pass-pill--muted">무료</span>
                    <h3 className="cp-sub-pass-plan-simple-title">프리 이용권</h3>
                    <div className="cp-sub-pass-plan-period-block">
                      <div className="cp-sub-pass-plan-period-label">이용권 활성 기간</div>
                      <div className="cp-sub-pass-plan-period-value">제한 없음</div>
                    </div>
                    <div className="cp-sub-pass-plan-price-stack">
                      <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                      <p className="cp-sub-pass-plan-hero-price">FREE</p>
                      {planCardInUse.freeInUse ? (
                        <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--in-use">
                          이용중
                        </span>
                      ) : (
                        <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--unavailable">
                          이용 불가
                        </span>
                      )}
                    </div>
                    <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                    <div
                      ref={planMidFreeRef}
                      className="cp-sub-pass-plan-mid-between-dashes"
                      style={planMidSlotPx > 0 ? { minHeight: planMidSlotPx } : undefined}
                    >
                      <div className="cp-sub-pass-plan-desc-stack cp-sub-pass-plan-desc-stack--mid">
                        <p className="cp-sub-pass-plan-simple-desc cp-sub-pass-plan-simple-desc--headline">
                          AI 기능 제외 모든 기능 이용가능!
                        </p>
                      </div>
                    </div>
                    <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                    <div className="cp-sub-pass-plan-desc-stack" />
                  </div>
                )}

                {purchaseUiLocked ? (
                  <button
                    type="button"
                    role="listitem"
                    className="cp-sub-pass-plan-simple-col cp-sub-pass-plan-simple-col--buy cp-sub-pass-plan-simple-col--beta-request"
                    title="2026학년도 이용권 (베타 테스트 코드 요청)"
                    aria-label="2026학년도 이용권 (베타 테스트 코드 요청)"
                    onClick={openBetaCodeRequestModal}
                  >
                    <span className="cp-pass-ticket-tier cp-pass-ticket-tier--plus" aria-hidden>
                      PLUS 이용권
                    </span>
                    <span className="cp-sub-pass-pill cp-sub-pass-pill--accent">베타</span>
                    <h3 className="cp-sub-pass-plan-simple-title cp-sub-pass-plan-simple-title--two-line">
                      <span>2026학년도 이용권</span>
                      <span className="cp-sub-pass-plan-simple-title-sub">(베타 테스트 코드 요청)</span>
                    </h3>
                    <div className="cp-sub-pass-plan-period-block">
                      <div className="cp-sub-pass-plan-period-label">이용권 활성 기간</div>
                      <div className="cp-sub-pass-plan-period-value">안내 후 적용</div>
                    </div>
                    <div className="cp-sub-pass-plan-price-stack">
                      <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                      <p className="cp-sub-pass-plan-hero-price">CODE</p>
                      <span className="cp-sub-pass-plan-action-badge">코드 요청</span>
                    </div>
                    <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                    <div
                      ref={planMidPaidEventRef}
                      className="cp-sub-pass-plan-mid-between-dashes"
                      style={planMidSlotPx > 0 ? { minHeight: planMidSlotPx } : undefined}
                    >
                      <div className="cp-sub-pass-plan-desc-stack cp-sub-pass-plan-desc-stack--mid">
                        <p className="cp-sub-pass-plan-simple-desc cp-sub-pass-plan-simple-desc--headline">
                          모든 기능 이용가능!
                        </p>
                      </div>
                    </div>
                    <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                    <div className="cp-sub-pass-plan-desc-stack" />
                  </button>
                ) : null}

                {!purchaseUiLocked ? (
                  <>
                {paidEventProductCards.map((card, cardIdx) => {
                  const soldOut = isPaidEventProductSoldOut(card.product);
                  const locked = planCardInUse.suppressOtherPasses || soldOut;
                  return (
                    <button
                      key={card.product.id}
                      type="button"
                      role="listitem"
                      className={`cp-sub-pass-plan-simple-col cp-sub-pass-plan-simple-col--buy cp-sub-pass-plan-simple-col--event${locked ? ' cp-sub-pass-plan-simple-col--locked cp-sub-pass-plan-simple-col--next-year' : ''}`}
                      disabled={locked}
                      title={
                        soldOut
                          ? '구매 가능 인원이 모두 소진되었습니다.'
                          : planCardInUse.suppressOtherPasses
                            ? '다른 이용권을 이용 중이라 구매할 수 없습니다.'
                            : `${card.product.eventName} 구매 (일괄 ${formatKrw(card.product.flatAmountWon)}원)`
                      }
                      aria-label={`${card.product.eventName} 구매`}
                      onClick={() => openPaidEventProductPurchase(card.product)}
                    >
                      <span className="cp-pass-ticket-tier cp-pass-ticket-tier--plus" aria-hidden>
                        PLUS 이용권
                      </span>
                      <span className="cp-sub-pass-pill cp-sub-pass-pill--accent">유료 이벤트</span>
                      <h3 className="cp-sub-pass-plan-simple-title">{card.product.eventName}</h3>
                      <div className="cp-sub-pass-plan-period-block">
                        <div className="cp-sub-pass-plan-period-label">이용권 활성 기간</div>
                        <div className="cp-sub-pass-plan-period-value">
                          {formatDateKo(card.periodStart)} ~ {formatDateKo(card.periodEnd)}
                        </div>
                      </div>
                      <div className="cp-sub-pass-plan-price-stack">
                        {card.strike ? (
                          <p className="cp-sub-pass-plan-list-price-struck" aria-hidden="false">
                            {formatKrw(card.listWon)}원
                          </p>
                        ) : (
                          <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                        )}
                        <p className="cp-sub-pass-plan-hero-price">
                          {formatKrw(card.product.flatAmountWon)}원
                        </p>
                        {planCardInUse.suppressOtherPasses ? (
                          <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--unavailable">
                            이용 불가
                          </span>
                        ) : soldOut ? (
                          <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--unavailable">
                            매진
                          </span>
                        ) : (
                          <span className="cp-sub-pass-plan-action-badge">이용권 구입</span>
                        )}
                      </div>
                      <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                      <div
                        ref={cardIdx === 0 ? planMidPaidEventRef : undefined}
                        className="cp-sub-pass-plan-mid-between-dashes"
                        style={planMidSlotPx > 0 ? { minHeight: planMidSlotPx } : undefined}
                      >
                        <div className="cp-sub-pass-plan-desc-stack cp-sub-pass-plan-desc-stack--mid">
                          <p className="cp-sub-pass-plan-simple-desc">
                            남은 기간과 관계없이 일괄 {formatKrw(card.product.flatAmountWon)}원이
                            적용됩니다.
                          </p>
                          <p
                            className={`cp-sub-pass-plan-event-limit${soldOut ? ' cp-sub-pass-plan-event-limit--sold-out' : ''}`}
                          >
                            {soldOut ? '매진' : `${card.product.activeUserCount}개 한정!`}
                          </p>
                        </div>
                      </div>
                      <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                      <div className="cp-sub-pass-plan-desc-stack" />
                    </button>
                  );
                })}

                <button
                  type="button"
                  role="listitem"
                  className={`cp-sub-pass-plan-simple-col cp-sub-pass-plan-simple-col--buy${planCardInUse.leftInUse ? ' cp-sub-pass-plan-simple-col--current' : ''}${!passPlanUi.purchasableLeft || (planCardInUse.suppressOtherPasses && !planCardInUse.leftInUse) ? ' cp-sub-pass-plan-simple-col--locked' : ''}${planCardInUse.suppressOtherPasses && !planCardInUse.leftInUse ? ' cp-sub-pass-plan-simple-col--next-year' : ''}`}
                  disabled={
                    planCardInUse.leftInUse ||
                    !passPlanUi.purchasableLeft ||
                    (planCardInUse.suppressOtherPasses && !planCardInUse.leftInUse)
                  }
                  title={
                    planCardInUse.leftInUse
                      ? planCardInUse.leftGroupHint
                        ? '현재 이용 중(그룹 쿠폰)입니다.'
                        : '현재 이용 중인 이용권입니다.'
                      : planCardInUse.suppressOtherPasses
                        ? '다른 이용권을 이용 중이라 구매할 수 없습니다.'
                        : !passPlanUi.purchasableLeft
                          ? `${passPlanUi.leftSy}학년도 이용권이 이미 적용되어, 해당 학년도 개별 이용권을 추가로 구매 적용할 수 없습니다.`
                          : `${passPlanUi.leftSy}학년도 개별 이용권 구매`
                  }
                  aria-label={`${passPlanUi.leftSy}학년도 개별 이용권 구매`}
                  onClick={() => openIndividualModalForSchoolYear(passPlanUi.leftSy)}
                >
                  <span className="cp-pass-ticket-tier cp-pass-ticket-tier--plus" aria-hidden>
                    PLUS 이용권
                  </span>
                  <span className="cp-sub-pass-pill cp-sub-pass-pill--accent">유료</span>
                  <h3 className="cp-sub-pass-plan-simple-title">{passPlanUi.leftSy}학년도 이용권</h3>
                  <div className="cp-sub-pass-plan-period-block">
                    <div className="cp-sub-pass-plan-period-label">이용권 활성 기간</div>
                    <div className="cp-sub-pass-plan-period-value">
                      {formatDateKo(passPlanUi.leftW.start)} ~ {formatDateKo(passPlanUi.leftW.end)}
                    </div>
                  </div>
                  <div className="cp-sub-pass-plan-price-stack">
                    {passPlanUi.strikeLeft ? (
                      <p className="cp-sub-pass-plan-list-price-struck" aria-hidden="false">
                        {formatKrw(passPlanUi.fullYearLeft.totalWon)}원
                      </p>
                    ) : (
                      <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                    )}
                    <p className="cp-sub-pass-plan-hero-price">{formatKrw(passPlanUi.pageLeft.totalWon)}원</p>
                    {planCardInUse.leftInUse ? (
                      <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--in-use">
                        이용중
                      </span>
                    ) : !passPlanUi.purchasableLeft || planCardInUse.suppressOtherPasses ? (
                      <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--unavailable">
                        이용 불가
                      </span>
                    ) : (
                      <span className="cp-sub-pass-plan-action-badge">이용권 구입</span>
                    )}
                  </div>
                  <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                  <div
                    ref={planMidLeftRef}
                    className="cp-sub-pass-plan-mid-between-dashes"
                    style={planMidSlotPx > 0 ? { minHeight: planMidSlotPx } : undefined}
                  >
                    {planCardInUse.leftInUseCoverageNote ? (
                      <div className="cp-sub-pass-plan-desc-stack cp-sub-pass-plan-desc-stack--mid">
                        <p className="cp-sub-pass-plan-simple-desc">해당 이용중 이용권은</p>
                        <p className="cp-sub-pass-plan-simple-desc">
                          유료 이벤트 이용권, 일반 유료 이용권 등을
                        </p>
                        <p className="cp-sub-pass-plan-simple-desc">모두 포함합니다.</p>
                        <p className="cp-sub-pass-plan-simple-desc">
                          이용중인 상세 이용권을 확인하고 싶으면
                        </p>
                        <p className="cp-sub-pass-plan-simple-desc">
                          상단 이용권 블럭 및 결제 내역을 확인하세요.
                        </p>
                      </div>
                    ) : passPlanUi.strikeLeft ? (
                      <div className="cp-sub-pass-plan-period-block cp-sub-pass-plan-period-block--sub">
                        <div className="cp-sub-pass-plan-period-label">부분 적용 기간</div>
                        <div className="cp-sub-pass-plan-period-value">
                          {formatDateKo(passPlanUi.pageLeft.periodStart)} ~{' '}
                          {formatDateKo(passPlanUi.pageLeft.billedPeriodEndInclusive)}
                        </div>
                        <div className="cp-sub-pass-plan-period-note">(오늘 결제 기준)</div>
                      </div>
                    ) : null}
                  </div>
                  <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                  <div className="cp-sub-pass-plan-desc-stack" />
                </button>

                <button
                  type="button"
                  role="listitem"
                  className={`cp-sub-pass-plan-simple-col cp-sub-pass-plan-simple-col--buy${planCardInUse.rightInUse ? ' cp-sub-pass-plan-simple-col--current' : ''}${passPlanUi.canBuyRight && passPlanUi.purchasableRight && !(planCardInUse.suppressOtherPasses && !planCardInUse.rightInUse) ? '' : ' cp-sub-pass-plan-simple-col--locked'}${!passPlanUi.canBuyRight || (planCardInUse.suppressOtherPasses && !planCardInUse.rightInUse) ? ' cp-sub-pass-plan-simple-col--next-year' : ''}`}
                  disabled={
                    planCardInUse.rightInUse ||
                    !passPlanUi.canBuyRight ||
                    !passPlanUi.purchasableRight ||
                    (planCardInUse.suppressOtherPasses && !planCardInUse.rightInUse)
                  }
                  title={
                    planCardInUse.rightInUse
                      ? planCardInUse.rightGroupHint
                        ? '현재 이용 중(그룹 쿠폰)입니다.'
                        : '현재 이용 중인 이용권입니다.'
                      : planCardInUse.suppressOtherPasses
                        ? '다른 이용권을 이용 중이라 구매할 수 없습니다.'
                        : !passPlanUi.canBuyRight
                          ? `해당 이용권은 ${passPlanUi.rightSy}년 1월부터 구매 가능합니다`
                          : !passPlanUi.purchasableRight
                            ? `${passPlanUi.rightSy}학년도 이용권이 이미 적용되어, 해당 학년도 개별 이용권을 추가로 구매 적용할 수 없습니다.`
                            : `${passPlanUi.rightSy}학년도 개별 이용권 구매`
                  }
                  aria-label={`${passPlanUi.rightSy}학년도 개별 이용권 구매`}
                  onClick={() => openIndividualModalForSchoolYear(passPlanUi.rightSy)}
                >
                  <span className="cp-pass-ticket-tier cp-pass-ticket-tier--plus" aria-hidden>
                    PLUS 이용권
                  </span>
                  <span className="cp-sub-pass-pill cp-sub-pass-pill--accent">유료</span>
                  <h3 className="cp-sub-pass-plan-simple-title">{passPlanUi.rightSy}학년도 이용권</h3>
                  <div className="cp-sub-pass-plan-period-block">
                    <div className="cp-sub-pass-plan-period-label">이용권 활성 기간</div>
                    <div className="cp-sub-pass-plan-period-value">
                      {formatDateKo(passPlanUi.rightW.start)} ~ {formatDateKo(passPlanUi.rightW.end)}
                    </div>
                  </div>
                  <div className="cp-sub-pass-plan-price-stack">
                    {!passPlanUi.canBuyRight ? (
                      <>
                        <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                        <p className="cp-sub-pass-plan-hero-price cp-sub-pass-plan-hero-price--ref">
                          {formatKrw(passPlanUi.fullYearRight.totalWon)}원
                        </p>
                      </>
                    ) : passPlanUi.strikeRight ? (
                      <>
                        <p className="cp-sub-pass-plan-list-price-struck" aria-hidden="false">
                          {formatKrw(passPlanUi.fullYearRight.totalWon)}원
                        </p>
                        <p className="cp-sub-pass-plan-hero-price">{formatKrw(passPlanUi.pageRight.totalWon)}원</p>
                      </>
                    ) : (
                      <>
                        <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                        <p className="cp-sub-pass-plan-hero-price">{formatKrw(passPlanUi.pageRight.totalWon)}원</p>
                      </>
                    )}
                    {planCardInUse.rightInUse ? (
                      <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--in-use">
                        이용중
                      </span>
                    ) : !passPlanUi.canBuyRight ||
                      !passPlanUi.purchasableRight ||
                      planCardInUse.suppressOtherPasses ? (
                      <span className="cp-sub-pass-plan-action-badge cp-sub-pass-plan-action-badge--unavailable">
                        이용 불가
                      </span>
                    ) : (
                      <span className="cp-sub-pass-plan-action-badge">이용권 구입</span>
                    )}
                  </div>
                  <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                  <div
                    ref={planMidRightRef}
                    className="cp-sub-pass-plan-mid-between-dashes"
                    style={planMidSlotPx > 0 ? { minHeight: planMidSlotPx } : undefined}
                  >
                    {planCardInUse.rightInUseCoverageNote ? (
                      <div className="cp-sub-pass-plan-desc-stack cp-sub-pass-plan-desc-stack--mid">
                        <p className="cp-sub-pass-plan-simple-desc">해당 이용중 이용권은</p>
                        <p className="cp-sub-pass-plan-simple-desc">
                          유료 이벤트 이용권, 일반 유료 이용권 등을
                        </p>
                        <p className="cp-sub-pass-plan-simple-desc">모두 포함합니다.</p>
                        <p className="cp-sub-pass-plan-simple-desc">
                          이용중인 상세 이용권을 확인하고 싶으면
                        </p>
                        <p className="cp-sub-pass-plan-simple-desc">
                          상단 이용권 블럭 및 결제 내역을 확인하세요.
                        </p>
                      </div>
                    ) : !passPlanUi.canBuyRight ? (
                      <p className="cp-sub-pass-plan-next-locked-msg" role="status">
                        <span className="cp-sub-pass-plan-next-locked-line">해당 이용권은</span>
                        <span className="cp-sub-pass-plan-next-locked-line">
                          {passPlanUi.rightSy}년 1월부터
                        </span>
                        <span className="cp-sub-pass-plan-next-locked-line">구매 가능합니다</span>
                      </p>
                    ) : passPlanUi.strikeRight ? (
                      <div className="cp-sub-pass-plan-period-block cp-sub-pass-plan-period-block--sub">
                        <div className="cp-sub-pass-plan-period-label">부분 적용 기간</div>
                        <div className="cp-sub-pass-plan-period-value">
                          {formatDateKo(passPlanUi.pageRight.periodStart)} ~{' '}
                          {formatDateKo(passPlanUi.pageRight.billedPeriodEndInclusive)}
                        </div>
                        <div className="cp-sub-pass-plan-period-note">(오늘 결제 기준)</div>
                      </div>
                    ) : null}
                  </div>
                  <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                  <div className="cp-sub-pass-plan-desc-stack" />
                </button>
                  </>
                ) : null}
              </div>

              {!purchaseUiLocked && !passPlanUi.purchasableLeft ? (
                <p className="cp-sub-pass-ind-coverage-blocked-msg" role="status">
                  {passPlanUi.leftSy}학년도 이용권이 이미 적용되어, 해당 학년도 개별 이용권을 추가로 구매 적용할 수
                  없습니다.
                </p>
              ) : null}

              {!purchaseUiLocked ? (
              <button
                type="button"
                className="cp-sub-pass-plan-simple-col cp-sub-pass-plan-simple-col--buy cp-sub-pass-plan-simple-col--group"
                title={`${passPlanUi.leftSy}학년도 그룹 이용권 구매`}
                aria-label={`${passPlanUi.leftSy}학년도 그룹 이용권 구매`}
                onClick={openGroupModal}
              >
                <span className="cp-pass-ticket-tier cp-pass-ticket-tier--plus" aria-hidden>
                  PLUS 이용권
                </span>
                <span className="cp-sub-pass-pill cp-sub-pass-pill--accent">유료</span>
                <h3 className="cp-sub-pass-plan-simple-title">
                  {passPlanUi.leftSy}학년도 그룹 이용권 구매
                </h3>
                <div className="cp-sub-pass-plan-period-block">
                  <div className="cp-sub-pass-plan-period-label">이용권 활성 기간</div>
                  <div className="cp-sub-pass-plan-period-value">
                    {formatDateKo(passPlanUi.leftW.start)} ~ {formatDateKo(passPlanUi.leftW.end)}
                  </div>
                </div>
                <div className="cp-sub-pass-plan-price-stack">
                  {passPlanUi.groupStrikeLeft ? (
                    <p className="cp-sub-pass-plan-list-price-struck" aria-hidden="false">
                      {formatKrw(passPlanUi.fullYearLeft.totalWon)}원
                    </p>
                  ) : (
                    <div className="cp-sub-pass-plan-strike-placeholder" aria-hidden />
                  )}
                  <p className="cp-sub-pass-plan-hero-price">
                    {formatKrw(passPlanUi.groupPageLeft.totalWon)}원~
                  </p>
                  <span className="cp-sub-pass-plan-action-badge">이용권 구입</span>
                </div>
                <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                <div className="cp-sub-pass-plan-mid-between-dashes">
                  <div className="cp-sub-pass-plan-period-block cp-sub-pass-plan-period-block--sub">
                    <div className="cp-sub-pass-plan-period-label">부분 적용 기간</div>
                    <div className="cp-sub-pass-plan-period-value">
                      {formatDateKo(passPlanUi.groupPageLeft.periodStart)} ~{' '}
                      {formatDateKo(passPlanUi.groupPageLeft.billedPeriodEndInclusive)}
                    </div>
                    <div className="cp-sub-pass-plan-period-note">(오늘 결제 기준)</div>
                  </div>
                </div>
                <div className="cp-sub-pass-plan-section-dash" aria-hidden />
                <div className="cp-sub-pass-plan-desc-stack">
                  <p className="cp-sub-pass-plan-simple-desc">
                    오늘 기준 1인당 예상액 × 구매 이용권 수로 결제됩니다.
                  </p>
                  <p className="cp-sub-pass-plan-simple-desc">
                    같은 코드로 이용권 수만큼 서로 다른 계정에서 등록할 수 있습니다.
                  </p>
                  <p className="cp-sub-pass-plan-simple-desc">
                    결제만으로는 계정에 자동 반영되지 않으며, 「쿠폰 코드 등록」이 필요합니다.
                  </p>
                </div>
              </button>
              ) : null}

              {!purchaseUiLocked && passPlanUi.canBuyRight && !passPlanUi.purchasableRight ? (
                <p className="cp-sub-pass-ind-coverage-blocked-msg" role="status">
                  {passPlanUi.rightSy}학년도 이용권이 이미 적용되어, 해당 학년도 개별 이용권을 추가로 구매 적용할 수
                  없습니다.
                </p>
              ) : null}
            </div>
          </div>
        </div>
        </>
        )}
      </section>

      <PassPlanComparisonTable passPlanKey={purchaseUiLocked ? 'beta' : 'individual'} />

      {betaCodeRequestOpen ? (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-sub-pass-beta-request-title"
          onClick={() => {
            if (!betaCodeRequestSubmitting) setBetaCodeRequestOpen(false);
          }}
        >
          <div
            className="cp-modal cp-sub-pass-coupon-modal-v2 cp-sub-pass-beta-request-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-sub-pass-coupon-modal-head">
              <h2 id="cp-sub-pass-beta-request-title" className="cp-sub-pass-coupon-modal-title">
                베타 테스트 이벤트 코드 요청
              </h2>
              <button
                type="button"
                className="cp-sub-pass-coupon-modal-close"
                aria-label="닫기"
                disabled={betaCodeRequestSubmitting}
                onClick={() => setBetaCodeRequestOpen(false)}
              >
                ×
              </button>
            </div>
            <p className="cp-sub-pass-coupon-modal-prompt">
              아래 정보를 남겨 주시면
              <br />
              확인 후 이벤트 이용권 코드를 메일로 발송해 드립니다.
            </p>
            <div className="cp-sub-pass-beta-request-fields">
              <label className="cp-sub-pass-beta-request-field" htmlFor="cp-sub-pass-beta-name">
                <span>이름</span>
                <input
                  id="cp-sub-pass-beta-name"
                  type="text"
                  autoComplete="name"
                  maxLength={80}
                  value={betaCodeRequestForm.fullName}
                  disabled={betaCodeRequestSubmitting}
                  onChange={(e) =>
                    setBetaCodeRequestForm((f) => ({ ...f, fullName: e.target.value }))
                  }
                />
              </label>
              <label className="cp-sub-pass-beta-request-field" htmlFor="cp-sub-pass-beta-email">
                <span>이메일</span>
                <input
                  id="cp-sub-pass-beta-email"
                  type="email"
                  autoComplete="email"
                  maxLength={320}
                  placeholder="ClassPlanner@edukodi.com"
                  value={betaCodeRequestForm.email}
                  disabled={betaCodeRequestSubmitting}
                  onChange={(e) =>
                    setBetaCodeRequestForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </label>
              <label className="cp-sub-pass-beta-request-field" htmlFor="cp-sub-pass-beta-org">
                <span>소속</span>
                <input
                  id="cp-sub-pass-beta-org"
                  type="text"
                  autoComplete="organization"
                  maxLength={200}
                  placeholder="학교 기관 연구회 명"
                  value={betaCodeRequestForm.organization}
                  disabled={betaCodeRequestSubmitting}
                  onChange={(e) =>
                    setBetaCodeRequestForm((f) => ({ ...f, organization: e.target.value }))
                  }
                />
              </label>
              <label className="cp-sub-pass-beta-request-field" htmlFor="cp-sub-pass-beta-note">
                <span>요청 메모 (선택)</span>
                <textarea
                  id="cp-sub-pass-beta-note"
                  rows={3}
                  maxLength={1000}
                  placeholder="프로그램의 어떤 부분에 관심이 있고 활용하고 싶은지, 기대하는 내용은 무엇인지 등 자유롭게 적어 주세요."
                  value={betaCodeRequestForm.note}
                  disabled={betaCodeRequestSubmitting}
                  onChange={(e) =>
                    setBetaCodeRequestForm((f) => ({ ...f, note: e.target.value }))
                  }
                />
              </label>
            </div>
            <div className="cp-sub-pass-beta-request-checks">
              <label className="cp-sub-pass-group-check" htmlFor="cp-sub-pass-beta-agree">
                <input
                  id="cp-sub-pass-beta-agree"
                  type="checkbox"
                  checked={betaCodeRequestForm.agreed}
                  disabled={betaCodeRequestSubmitting}
                  onChange={(e) =>
                    setBetaCodeRequestForm((f) => ({ ...f, agreed: e.target.checked }))
                  }
                />
                <span>
                  베타 테스터로 참여를 희망하며 이용 중 프로그램에 대한 피드백 제공과 자료 공유에
                  동의합니다.
                </span>
              </label>
            </div>
            {betaCodeRequestError ? (
              <p className="cp-sub-pass-beta-request-error" role="alert">
                {betaCodeRequestError}
              </p>
            ) : null}
            <div className="cp-sub-pass-coupon-modal-footer">
              <button
                type="button"
                className="cp-sub-pass-coupon-footer-btn cp-sub-pass-coupon-footer-btn--cancel"
                disabled={betaCodeRequestSubmitting}
                onClick={() => setBetaCodeRequestOpen(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-sub-pass-coupon-footer-btn cp-sub-pass-coupon-footer-btn--submit"
                disabled={!betaCodeRequestCanSubmit}
                title={
                  betaCodeRequestCanSubmit
                    ? undefined
                    : !betaCodeRequestForm.fullName.trim() ||
                        !simpleEmailValid(betaCodeRequestForm.email.trim()) ||
                        !betaCodeRequestForm.organization.trim()
                      ? '이름·이메일·소속을 모두 입력해 주세요.'
                      : !betaCodeRequestForm.agreed
                        ? '확인 사항에 동의해 주세요.'
                        : undefined
                }
                onClick={() => void submitBetaCodeRequestModal()}
              >
                {betaCodeRequestSubmitting ? '제출 중…' : '제출'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {couponModalOpen ? (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-sub-pass-coupon-modal-title"
          onClick={() => setCouponModalOpen(false)}
        >
          <div className="cp-modal cp-sub-pass-coupon-modal-v2" onClick={(e) => e.stopPropagation()}>
            <div className="cp-sub-pass-coupon-modal-head">
              <h2 id="cp-sub-pass-coupon-modal-title" className="cp-sub-pass-coupon-modal-title">
                쿠폰 코드 등록
              </h2>
              <button
                type="button"
                className="cp-sub-pass-coupon-modal-close"
                aria-label="닫기"
                onClick={() => setCouponModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div
              className="cp-pin-bar-tabs cp-pin-bar-tabs--3 cp-sub-pass-coupon-modal-kind-row"
              role="tablist"
              aria-label="쿠폰 종류"
            >
              <button
                type="button"
                role="tab"
                aria-selected={couponModalKind === 'group'}
                className={`cp-pin-bar-tab${couponModalKind === 'group' ? ' is-active' : ''}`}
                onClick={() => setCouponModalKind('group')}
              >
                그룹 이용권
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={couponModalKind === 'event'}
                className={`cp-pin-bar-tab${couponModalKind === 'event' ? ' is-active' : ''}`}
                onClick={() => setCouponModalKind('event')}
              >
                이벤트 이용권
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={couponModalKind === 'beta'}
                className={`cp-pin-bar-tab${couponModalKind === 'beta' ? ' is-active' : ''}`}
                onClick={() => setCouponModalKind('beta')}
              >
                베타 테스터 이용권
              </button>
            </div>
            <p className="cp-sub-pass-coupon-modal-prompt">
              {couponModalKind === 'group'
                ? '그룹 이용권 쿠폰 코드를 입력하세요'
                : couponModalKind === 'beta'
                  ? '베타 테스터 이용권 코드를 입력하세요'
                  : '이벤트 이용권 코드를 입력하세요'}
            </p>
            <label className="cp-visually-hidden" htmlFor="cp-sub-pass-coupon-code">
              {couponModalKind === 'group'
                ? '그룹 이용권 쿠폰 코드'
                : couponModalKind === 'beta'
                  ? '베타 테스터 이용권 코드'
                  : '이벤트 이용권 코드'}
            </label>
            <input
              id="cp-sub-pass-coupon-code"
              type="text"
              autoComplete="off"
              spellCheck={false}
              className="cp-sub-pass-coupon-code-input"
              placeholder={
                couponModalKind === 'group'
                  ? '예: GRP-ABCD2345EFGH'
                  : couponModalKind === 'beta'
                    ? '예: CBT-ABCD2345EFGH'
                    : '예: EVT-ABCD2345EFGH'
              }
              value={couponCodeInput}
              onChange={(e) => setCouponCodeInput(e.target.value)}
            />
            <p className="cp-sub-pass-coupon-modal-hint">
              {couponModalKind === 'group' ? (
                <>
                  구매·발급 안내된 코드와 동일하게 입력해 주세요. 이용권 수만큼 다른 계정에서도 같은 코드로 등록할 수
                  있습니다.
                </>
              ) : couponModalKind === 'beta' ? (
                <>관리자가 발급한 베타 테스터 이용권 코드(CBT-…)를 입력하면, 코드에 설정된 이용 기간이 적용됩니다.</>
              ) : (
                <>관리자가 발급한 이벤트 이용권 코드를 입력하면, 코드에 설정된 이용 기간이 적용됩니다.</>
              )}
            </p>
            <div className="cp-sub-pass-coupon-modal-footer">
              <button
                type="button"
                className="cp-sub-pass-coupon-footer-btn cp-sub-pass-coupon-footer-btn--cancel"
                onClick={() => setCouponModalOpen(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-sub-pass-coupon-footer-btn cp-sub-pass-coupon-footer-btn--submit"
                onClick={submitCouponModal}
              >
                등록
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* 개인 구매 모달 */}
      {modal?.kind === 'individual' ? (
        <div
          className="cp-modal-backdrop cp-sub-pass-purchase-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-sub-pass-modal-title"
          onClick={closeModal}
        >
          <div className="cp-modal cp-sub-pass-purchase-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="cp-sub-pass-modal-title" className="cp-sub-pass-modal-title">
              단일 이용자 구매
            </h2>

            <div className="cp-sub-pass-ind-period-block">
              <p className="cp-sub-pass-ind-period-label">이용권 적용기간</p>
              <p className="cp-sub-pass-ind-period-value">
                {formatDateDot(modal.pricing.periodStart)} ~{' '}
                {formatDateDot(modal.pricing.billedPeriodEndInclusive)}
              </p>
            </div>

            <div className="cp-sub-pass-purchase-total-hero" aria-live="polite">
              <p className="cp-sub-pass-purchase-total-eyebrow">결제 예정 총액</p>
              <p className="cp-sub-pass-purchase-total-val">{formatKrw(modal.pricing.totalWon)}원</p>
            </div>

            <p className="cp-sub-pass-breakdown-section-title" id="cp-sub-pass-ind-breakdown-title">
              결제금액 산출 내역
            </p>
            <div
              className="cp-sub-pass-pricing-lines-text"
              role="region"
              aria-labelledby="cp-sub-pass-ind-breakdown-title"
            >
              {aggregateIndividualPricingModalLines(modal.pricing).map((line, i) => (
                <p key={`ind-price-line-${i}`} className="cp-sub-pass-pricing-lines-text-line">
                  {line}
                </p>
              ))}
              <p className="cp-sub-pass-pricing-lines-text-foot">
                월 {formatKrw(PAID_PLAN_MONTHLY_WON)}원 · 일 {formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원 (한 달 최대{' '}
                {formatKrw(PAID_PLAN_MONTHLY_WON)}원)
              </p>
            </div>

            <div className="cp-sub-pass-group-checks">
              <label className="cp-sub-pass-group-check">
                <input
                  type="checkbox"
                  checked={individualAgreeRefund}
                  onChange={(e) => setIndividualAgreeRefund(e.target.checked)}
                />
                <span>
                  환불은 7일 이내의 경우 실제 사용 일수 × 1일 이용료(
                  {formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원)를 제외한 잔액이 환불되며, 7일 경과의 경우 실제 사용
                  일수 × 1일 이용료({formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원)를 제외한 잔액에서{' '}
                  <strong>10% 수수료</strong>를 추가로 공제하고 환불됩니다.
                </span>
              </label>
              <p className="cp-sub-pass-refund-policy-link">
                <a href={LEGAL_REFUND_HREF} target="_blank" rel="noopener noreferrer">
                  이용약관 환불 규정 보기
                </a>
              </p>
            </div>

            <div className="cp-modal-footer cp-sub-pass-purchase-footer">
              {requireLoginForCheckout ? (
                <p className="cp-sub-pass-purchase-login-hint" role="status">
                  로그인 후 결재를 진행해주세요.
                </p>
              ) : null}
              <div className="cp-sub-pass-purchase-footer-actions">
                <button type="button" className="cp-btn-secondary" onClick={closeModal}>
                  취소
                </button>
                <button
                  type="button"
                  className="cp-btn-primary"
                  disabled={
                    requireLoginForCheckout ||
                    modal.pricing.totalWon <= 0 ||
                    !individualAgreeRefund
                  }
                  title={
                    requireLoginForCheckout
                      ? '로그인 후 결재를 진행해주세요.'
                      : modal.pricing.totalWon <= 0
                        ? '추가로 납부할 이용 일수가 없습니다.'
                        : !individualAgreeRefund
                          ? '환불 정책 안내를 확인하고 동의해 주세요.'
                          : undefined
                  }
                  onClick={confirmIndividualPurchase}
                >
                  결제하기
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* 이벤트 이용권 구매 모달 */}
      {modal?.kind === 'event' ? (
        <div
          className="cp-modal-backdrop cp-sub-pass-purchase-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-sub-pass-event-modal-title"
          onClick={closeModal}
        >
          <div className="cp-modal cp-sub-pass-purchase-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="cp-sub-pass-event-modal-title" className="cp-sub-pass-modal-title">
              {modal.title} 구매
            </h2>

            <div className="cp-sub-pass-ind-period-block">
              <p className="cp-sub-pass-ind-period-label">이용권 적용기간</p>
              <p className="cp-sub-pass-ind-period-value">
                {formatDateDot(modal.periodStart)} ~ {formatDateDot(modal.periodEnd)}
              </p>
            </div>

            <div className="cp-sub-pass-purchase-total-hero" aria-live="polite">
              <p className="cp-sub-pass-purchase-total-eyebrow">결제 예정 총액</p>
              <p className="cp-sub-pass-purchase-total-val">{formatKrw(modal.totalWon)}원</p>
            </div>

            <p className="cp-sub-pass-breakdown-section-title" id="cp-sub-pass-event-breakdown-title">
              결제금액 산출 내역
            </p>
            <div
              className="cp-sub-pass-pricing-lines-text"
              role="region"
              aria-labelledby="cp-sub-pass-event-breakdown-title"
            >
              <p className="cp-sub-pass-pricing-lines-text-line">
                {modal.durationMonths}개월 : {formatKrw(modal.totalWon)}원
              </p>
              <p className="cp-sub-pass-pricing-lines-text-foot">
                일 {formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원 · 부분 적용 없이 일괄{' '}
                {formatKrw(modal.totalWon)}원
              </p>
            </div>

            <div className="cp-sub-pass-group-checks">
              <label className="cp-sub-pass-group-check">
                <input
                  type="checkbox"
                  checked={individualAgreeRefund}
                  onChange={(e) => setIndividualAgreeRefund(e.target.checked)}
                />
                <span>
                  환불은 7일 이내의 경우 실제 사용 일수 × 1일 이용료(
                  {formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원)를 제외한 잔액이 환불되며, 7일 경과의 경우 실제 사용
                  일수 × 1일 이용료({formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원)를 제외한 잔액에서{' '}
                  <strong>10% 수수료</strong>를 추가로 공제하고 환불됩니다.
                </span>
              </label>
              <p className="cp-sub-pass-refund-policy-link">
                <a href={LEGAL_REFUND_HREF} target="_blank" rel="noopener noreferrer">
                  이용약관 환불 규정 보기
                </a>
              </p>
            </div>

            <div className="cp-modal-footer cp-sub-pass-purchase-footer">
              {requireLoginForCheckout ? (
                <p className="cp-sub-pass-purchase-login-hint" role="status">
                  로그인 후 결재를 진행해주세요.
                </p>
              ) : null}
              <div className="cp-sub-pass-purchase-footer-actions">
                <button type="button" className="cp-btn-secondary" onClick={closeModal}>
                  취소
                </button>
                <button
                  type="button"
                  className="cp-btn-primary"
                  disabled={requireLoginForCheckout || !individualAgreeRefund}
                  title={
                    requireLoginForCheckout
                      ? '로그인 후 결재를 진행해주세요.'
                      : !individualAgreeRefund
                        ? '환불 정책 안내를 확인하고 동의해 주세요.'
                        : undefined
                  }
                  onClick={confirmEventPurchase}
                >
                  결제하기
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}


      {/* 그룹 이용권 구매 모달 (에듀코디식) */}
      {modal?.kind === 'group' ? (
        <div
          className="cp-modal-backdrop cp-sub-pass-purchase-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-sub-pass-group-modal-title"
          onClick={closeModal}
        >
          <div className="cp-modal cp-sub-pass-group-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-sub-pass-group-modal-head">
              <h2 id="cp-sub-pass-group-modal-title" className="cp-sub-pass-group-modal-title">
                유료 플랜 · 그룹 이용권 구입
              </h2>
              <button type="button" className="cp-sub-pass-group-modal-close" onClick={closeModal} aria-label="닫기">
                ×
              </button>
            </div>

            <div className="cp-sub-pass-group-field cp-sub-pass-group-field--billing">
              <label className="cp-sub-pass-group-field-label" htmlFor="cp-sub-pass-grp-billing-start">
                유료 플랜 시작일
              </label>
              {groupBillingDateBounds ? (
                <div className="cp-sub-pass-billing-date-field">
                  <input
                    id="cp-sub-pass-grp-billing-start"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="YYYY.MM.DD"
                    className="cp-sub-pass-billing-month-select cp-sub-pass-billing-date-input"
                    value={groupBillingStartDraft}
                    onChange={(e) => onGroupBillingStartDraftChange(e.target.value)}
                    onBlur={commitGroupBillingStartDraft}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                  <input
                    type="date"
                    className="cp-sub-pass-billing-date-picker"
                    min={groupBillingDateBounds.minIso}
                    max={groupBillingDateBounds.maxIso}
                    value={groupForm.billingStartIso}
                    onChange={(e) => {
                      if (!e.target.value) {
                        setGroupBillingStartDraft('');
                        return;
                      }
                      updateGroupBillingStartIso(e.target.value);
                    }}
                    aria-label="달력에서 유료 플랜 시작일 선택"
                    tabIndex={-1}
                  />
                </div>
              ) : null}
            </div>

            <div className="cp-sub-pass-group-field">
              <label className="cp-sub-pass-group-field-label" htmlFor="cp-grp-name">
                그룹명
              </label>
              <input
                id="cp-grp-name"
                className="cp-sub-pass-group-input"
                placeholder="예: 2026 봄학기 3학년"
                value={groupForm.groupName}
                onChange={(e) => setGroupForm((f) => ({ ...f, groupName: e.target.value }))}
                autoComplete="organization"
              />
            </div>

            <div className="cp-sub-pass-group-field">
              <label className="cp-sub-pass-group-field-label" htmlFor="cp-grp-email">
                쿠폰 수신 이메일
              </label>
              <input
                id="cp-grp-email"
                type="email"
                className="cp-sub-pass-group-input"
                placeholder="example@domain.com"
                value={groupForm.email}
                onChange={(e) => setGroupForm((f) => ({ ...f, email: e.target.value }))}
                autoComplete="email"
              />
            </div>

            <div className="cp-sub-pass-group-field cp-sub-pass-group-field--qty">
              <span className="cp-sub-pass-group-field-label" id="cp-grp-qty-label">
                이용권 수
              </span>
              <div className="cp-sub-pass-qty-row" role="group" aria-labelledby="cp-grp-qty-label">
                <button
                  type="button"
                  className="cp-sub-pass-qty-btn"
                  onClick={() =>
                    setGroupForm((f) => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))
                  }
                  aria-label="수량 감소"
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  max={99}
                  step={1}
                  className="cp-sub-pass-qty-val cp-sub-pass-qty-input"
                  aria-label="이용권 수 직접 입력"
                  value={groupForm.quantity}
                  onChange={(e) => {
                    const t = e.target.value;
                    if (t === '') return;
                    const n = Number.parseInt(t, 10);
                    if (!Number.isFinite(n)) return;
                    setGroupForm((f) => ({
                      ...f,
                      quantity: Math.min(99, Math.max(1, n)),
                    }));
                  }}
                  onBlur={(e) => {
                    const t = e.target.value;
                    if (t === '' || Number.parseInt(t, 10) < 1) {
                      setGroupForm((f) => ({ ...f, quantity: Math.max(1, f.quantity) }));
                    }
                  }}
                />
                <button
                  type="button"
                  className="cp-sub-pass-qty-btn"
                  onClick={() =>
                    setGroupForm((f) => ({ ...f, quantity: Math.min(99, f.quantity + 1) }))
                  }
                  aria-label="수량 증가"
                >
                  +
                </button>
              </div>
            </div>

            <div className="cp-sub-pass-ind-period-block">
              <p className="cp-sub-pass-ind-period-label">이용권 적용기간</p>
              <p className="cp-sub-pass-ind-period-value">
                {formatDateDot(modal.basePricing.periodStart)} ~{' '}
                {formatDateDot(modal.basePricing.billedPeriodEndInclusive)}
              </p>
            </div>

            <div
              className="cp-sub-pass-purchase-total-hero cp-sub-pass-group-purchase-total"
              aria-live="polite"
            >
              <p className="cp-sub-pass-purchase-total-eyebrow">결제 예정 금액</p>
              <p className="cp-sub-pass-purchase-total-val">{formatKrw(groupTotalWon)}원</p>
              <p className="cp-sub-pass-group-purchase-total-note">
                1인당 월 정액 합계 {formatKrw(modal.basePricing.totalWon)}원 × {groupForm.quantity}명
              </p>
            </div>

            <p className="cp-sub-pass-breakdown-section-title" id="cp-sub-pass-grp-breakdown-title">
              결제금액 산출 내역
            </p>
            <div
              className="cp-sub-pass-pricing-lines-text"
              role="region"
              aria-labelledby="cp-sub-pass-grp-breakdown-title"
            >
              {aggregateIndividualPricingModalLines(modal.basePricing).map((line, i) => (
                <p key={`grp-price-line-${i}`} className="cp-sub-pass-pricing-lines-text-line">
                  {line}
                </p>
              ))}
              <p className="cp-sub-pass-pricing-lines-text-line">
                1인당 {formatKrw(modal.basePricing.totalWon)}원 × {groupForm.quantity}명 ={' '}
                {formatKrw(groupTotalWon)}원
              </p>
              <p className="cp-sub-pass-pricing-lines-text-foot">
                월 {formatKrw(PAID_PLAN_MONTHLY_WON)}원 · 일 {formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원 (한 달 최대{' '}
                {formatKrw(PAID_PLAN_MONTHLY_WON)}원)
              </p>
            </div>

            <div className="cp-sub-pass-group-checks">
              <label className="cp-sub-pass-group-check">
                <input
                  type="checkbox"
                  checked={groupForm.agreeActivation}
                  onChange={(e) => setGroupForm((f) => ({ ...f, agreeActivation: e.target.checked }))}
                />
                <span>
                  표시된 이용권 적용 기간은 이 쿠폰을 적용할 때 등록한 각 계정에 적용됩니다. 결제만으로는 본 계정에
                  이용권 등록이 자동 반영되지 않으며, 반드시 <strong>「쿠폰 코드 등록」</strong>을 해야 합니다. 같은
                  코드로 구매한 이용권 수만큼 서로 다른 계정에서 등록할 수 있습니다.
                </span>
              </label>
              <label className="cp-sub-pass-group-check">
                <input
                  type="checkbox"
                  checked={groupForm.agreeRefund}
                  onChange={(e) => setGroupForm((f) => ({ ...f, agreeRefund: e.target.checked }))}
                />
                <span>
                  환불은 <strong>청약 철회(7일 이내·미사용)</strong>, <strong>미사용 환불(7일 경과)</strong>,{' '}
                  <strong>중도 해지(활성 이용권·사용 후)</strong> 규칙을 따릅니다. 활성 이용권이 없으면 7일 이내 전액·7일
                  경과 시 90%이며, 활성 이용권이 있으면 사용일 공제 후 잔액의 10% 위약금이 공제될 수 있습니다. (세부는
                  이용약관 환불 기준에 따릅니다.)
                </span>
              </label>
              <p className="cp-sub-pass-refund-policy-link">
                <a href={LEGAL_REFUND_HREF} target="_blank" rel="noopener noreferrer">
                  이용약관 환불 규정 보기
                </a>
              </p>
            </div>

            <div className="cp-modal-footer cp-sub-pass-purchase-footer">
              {requireLoginForCheckout ? (
                <p className="cp-sub-pass-purchase-login-hint" role="status">
                  로그인 후 결재를 진행해주세요.
                </p>
              ) : null}
              <div className="cp-sub-pass-purchase-footer-actions">
                <button type="button" className="cp-btn-secondary" onClick={closeModal}>
                  취소
                </button>
                <button
                  type="button"
                  className="cp-btn-primary"
                  disabled={requireLoginForCheckout || !groupPayEnabled || groupPurchaseSubmitting}
                  title={
                    requireLoginForCheckout ? '로그인 후 결재를 진행해주세요.' : undefined
                  }
                  onClick={() => void confirmGroupPurchase()}
                >
                  {groupPurchaseSubmitting ? '처리 중…' : '결제하기'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {refundModal && !isLandingPurchase ? (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-sub-pass-refund-title"
          onClick={() => setRefundModal(null)}
        >
          <div className="cp-modal cp-sub-pass-refund-modal" onClick={(e) => e.stopPropagation()}>
            {refundModal.step === 3 ? (
              <>
                <div className="cp-sub-pass-refund-modal-head">
                  <h2 id="cp-sub-pass-refund-title" className="cp-sub-pass-refund-modal-title">
                    환불 완료
                  </h2>
                  <button
                    type="button"
                    className="cp-sub-pass-refund-modal-close"
                    aria-label="닫기"
                    onClick={() => setRefundModal(null)}
                  >
                    ×
                  </button>
                </div>
                <p className="cp-sub-pass-refund-modal-lead">
                  환불 절차가 완료되었습니다. (데모: 실제 입금은 결제 수단·PG 연동 후 처리됩니다.)
                </p>
                <p className="cp-sub-pass-refund-modal-amount">
                  환불 금액 <strong>{formatKrw(refundModal.completedRefundWon ?? 0)}원</strong>
                </p>
                <div className="cp-sub-pass-refund-modal-footer">
                  <button type="button" className="cp-btn-primary" onClick={() => setRefundModal(null)}>
                    확인
                  </button>
                </div>
              </>
            ) : refundPreview ? (
              <>
                <div className="cp-sub-pass-refund-modal-head">
                  <h2 id="cp-sub-pass-refund-title" className="cp-sub-pass-refund-modal-title">
                    {refundModal.step === 1 ? '이용권 환불' : '환불 금액 확인'}
                  </h2>
                  <button
                    type="button"
                    className="cp-sub-pass-refund-modal-close"
                    aria-label="닫기"
                    onClick={() => setRefundModal(null)}
                  >
                    ×
                  </button>
                </div>

                {refundModal.step === 1 ? (
                  <>
                    <ul className="cp-sub-pass-refund-modal-bullets">
                      {refundPreview.lines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    <p className="cp-sub-pass-refund-modal-note">
                      {refundModal.kind === 'individual' ? (
                        <>
                          환불 금액은 <strong>청약 철회(7일 이내·미사용)</strong>, <strong>미사용 환불(7일 경과)</strong>,{' '}
                          <strong>중도 해지(사용 후)</strong> 중 해당 유형에 따라 산정됩니다. 위약금(10%)·이용일
                          공제가 포함될 수 있습니다. 이 화면은 안내용 예상액이며, 최종 환불액은 PG·카드사 안내를
                          따릅니다.
                        </>
                      ) : (
                        <>
                          환불 금액은 <strong>청약 철회(7일 이내·미사용)</strong>, <strong>미사용 환불(7일 경과)</strong>,{' '}
                          <strong>중도 해지(활성 이용권·사용 후)</strong> 중 해당 유형에 따라 산정됩니다. 위약금(10%)·이용일
                          공제가 포함될 수 있습니다. 이 화면은 안내용 예상액이며, 최종 환불액은 PG·카드사 안내를
                          따릅니다.
                        </>
                      )}
                    </p>
                    <label className="cp-sub-pass-refund-modal-check">
                      <input
                        type="checkbox"
                        checked={refundModal.agreed}
                        onChange={(e) =>
                          setRefundModal((m) => (m ? { ...m, agreed: e.target.checked } : null))
                        }
                      />
                      <span>위 안내를 확인했으며, 환불 절차를 진행합니다.</span>
                    </label>
                    <div className="cp-sub-pass-refund-modal-footer cp-sub-pass-refund-modal-footer--split">
                      <button type="button" className="cp-btn-secondary" onClick={() => setRefundModal(null)}>
                        취소
                      </button>
                      <button
                        type="button"
                        className="cp-btn-primary"
                        disabled={!refundModal.agreed}
                        onClick={() => setRefundModal((m) => (m ? { ...m, step: 2 } : null))}
                      >
                        다음
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <dl className="cp-sub-pass-refund-modal-breakdown">
                      <div>
                        <dt>
                          {refundModal.kind === 'group'
                            ? '결제액(그룹)'
                            : refundModal.kind === 'event'
                              ? '결제액(이벤트)'
                              : '결제액(단일)'}
                        </dt>
                        <dd>
                          {formatKrw(
                            (refundModal.kind === 'individual'
                              ? individualHistory.find((r) => r.id === refundModal.recordId)?.amountWon
                              : refundModal.kind === 'event'
                                ? eventHistory.find((r) => r.id === refundModal.recordId)?.amountWon
                                : groupHistory.find((r) => r.id === refundModal.recordId)?.totalWon) ?? 0
                          )}
                          원
                        </dd>
                      </div>
                      {refundPreview.usageDeductionWon > 0 ? (
                        <div>
                          <dt>
                            {refundModal.kind === 'group'
                              ? `활성 이용권 사용일 공제 (1일 ${formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원)`
                              : `이용일수 공제 (1일 ${formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원)`}
                          </dt>
                          <dd>-{formatKrw(refundPreview.usageDeductionWon)}원</dd>
                        </div>
                      ) : null}
                      {refundPreview.feeWon > 0 ? (
                        <div>
                          <dt>위약금(10%)</dt>
                          <dd>-{formatKrw(refundPreview.feeWon)}원</dd>
                        </div>
                      ) : null}
                      <div className="cp-sub-pass-refund-modal-breakdown--total">
                        <dt>예상 환불액</dt>
                        <dd>{formatKrw(refundPreview.refundWon)}원</dd>
                      </div>
                    </dl>
                    {refundPreview.fullRefundNoFee ? (
                      <p className="cp-sub-pass-refund-modal-note">
                        {refundModal.kind === 'individual'
                          ? '청약 철회에 해당합니다. 결제 후 7일 이내·미사용으로 결제액 전액이 환불됩니다.'
                          : '청약 철회에 해당합니다. 결제 후 7일 이내·활성 이용권 없음(미사용)으로 결제액 전액이 환불됩니다.'}
                      </p>
                    ) : null}
                    <div className="cp-sub-pass-refund-modal-footer cp-sub-pass-refund-modal-footer--split">
                      <button
                        type="button"
                        className="cp-btn-secondary"
                        onClick={() => setRefundModal((m) => (m ? { ...m, step: 1 } : null))}
                      >
                        이전
                      </button>
                      <button type="button" className="cp-btn-primary" onClick={completeRefund}>
                        환불 진행
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="cp-sub-pass-refund-modal-lead">내역을 찾을 수 없습니다.</p>
            )}
          </div>
        </div>
      ) : null}

      {testPayPending ? (
        <div
          className="cp-modal-backdrop cp-sub-pass-test-pay-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-sub-pass-test-pay-title"
          onClick={closeTestPayModal}
        >
          <div className="cp-modal cp-sub-pass-test-pay-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-sub-pass-test-pay-head">
              <h2 id="cp-sub-pass-test-pay-title" className="cp-sub-pass-test-pay-title">
                테스트 모드
              </h2>
              <button
                type="button"
                className="cp-sub-pass-test-pay-close"
                onClick={closeTestPayModal}
                aria-label="닫기"
              >
                ×
              </button>
            </div>
            <p className="cp-sub-pass-test-pay-label">테스트 결제 비밀번호를 입력하세요</p>
            <label className="cp-visually-hidden" htmlFor="cp-sub-pass-test-pay-password">
              테스트 결제 비밀번호
            </label>
            <input
              id="cp-sub-pass-test-pay-password"
              type="password"
              className="cp-sub-pass-test-pay-input"
              value={testPayPassword}
              autoFocus
              autoComplete="current-password"
              placeholder="비밀번호 입력"
              onChange={(e) => {
                setTestPayPassword(e.target.value);
                if (testPayError) setTestPayError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (testPayPassword.trim() && !groupPurchaseSubmitting) {
                    void confirmTestPayPassword();
                  }
                }
              }}
            />
            {testPayError ? (
              <p className="cp-sub-pass-test-pay-error" role="alert">
                {testPayError}
              </p>
            ) : (
              <p className="cp-sub-pass-test-pay-hint">
                테스트 모드에서는 실제 결제 없이 데모 이용권이 반영됩니다.
              </p>
            )}
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={closeTestPayModal}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                disabled={!testPayPassword.trim() || groupPurchaseSubmitting}
                onClick={() => void confirmTestPayPassword()}
              >
                {groupPurchaseSubmitting ? '처리 중…' : '확인'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
    </div>
    {!isLandingPurchase ? (
      <PlatformCompanyInfo className="landing-company-info--in-app" />
    ) : null}
    </>
  );

  return (
    <>
      {isLandingPurchase ? pageBody : <div className="cp-sub-pass-shell">{pageBody}</div>}
      {toastState ? (
        <AutoStatusToast
          state={toastState}
          className={
            modal || couponModalOpen || betaCodeRequestOpen || testPayPending || isLandingPurchase
              ? 'cp-toast-auto-over-modal'
              : undefined
          }
        />
      ) : null}
    </>
  );
};

export default SubscriptionPassPage;
